import React, { Component, useRef, useLayoutEffect } from 'react';
import { SaveButton, DeleteButton } from '../modalButtons';
import speechIcon from '../img/menuSpeech.svg';
import PromptPicker from '../promptPicker';

// ── Color system ───────────────────────────────────────────────────────────────
var ROOT_COLOR = '#2c3e50';
var BRANCH_COLORS = ['#1a4a6e', '#1a5c3a', '#6e1a2a', '#3d1a6e', '#1a5c5c', '#6e4a1a'];

function lightenHex(hex, amount) {
    var r = parseInt(hex.slice(1, 3), 16);
    var g = parseInt(hex.slice(3, 5), 16);
    var b = parseInt(hex.slice(5, 7), 16);
    r = Math.round(r + (255 - r) * amount);
    g = Math.round(g + (255 - g) * amount);
    b = Math.round(b + (255 - b) * amount);
    return '#' + r.toString(16).padStart(2, '0') + g.toString(16).padStart(2, '0') + b.toString(16).padStart(2, '0');
}

function computeColorMap(nodes, edges) {
    // Entry nodes have their own fixed style, so they're excluded from this graph
    // entirely — we don't want their single "next" link to consume a branch color
    // and squash all the downstream menus into one hue.
    var coloredNodes = nodes.filter(function(n) { return n.type !== 'entryNode'; });
    var coloredIds = {};
    coloredNodes.forEach(function(n) { coloredIds[n.id] = true; });
    var childMap = {}, hasParent = {};
    coloredNodes.forEach(function(n) { childMap[n.id] = []; });
    function canReach(from, to) {
        var queue = [from], seen = {}; seen[from] = true;
        while (queue.length) { var c = queue.shift(); if (c === to) return true; (childMap[c] || []).forEach(function(k) { if (!seen[k]) { seen[k] = true; queue.push(k); } }); }
        return false;
    }
    edges.forEach(function(e) {
        if (!coloredIds[e.source] || !coloredIds[e.target]) return;
        if (hasParent[e.target] || canReach(e.target, e.source)) return;
        childMap[e.source].push(e.target);
        hasParent[e.target] = true;
    });
    var colorMap = {};
    var roots = coloredNodes.filter(function(n) { return !hasParent[n.id]; });
    roots.forEach(function(root) {
        colorMap[root.id] = ROOT_COLOR;
        childMap[root.id].forEach(function(childId, i) {
            var base = BRANCH_COLORS[i % BRANCH_COLORS.length];
            (function assignColors(id, depth) {
                colorMap[id] = lightenHex(base, Math.min(depth, 4) * 0.13);
                childMap[id].forEach(function(kid) { assignColors(kid, depth + 1); });
            })(childId, 0);
        });
    });
    return colorMap;
}

// ── Layout constants ───────────────────────────────────────────────────────────
var NODE_W = 220;
var NODE_H = 140;
var H_GAP = 60;
var V_GAP = 60;

// ── Tree layout ────────────────────────────────────────────────────────────────
function buildLayout(nodes, edges, heights) {
    if (!nodes || nodes.length === 0) return { positions: {}, width: 0, height: 0 };
    heights = heights || {};
    function nodeH(id) { return heights[id] || NODE_H; }

    var childMap = {};
    var hasParent = {};
    nodes.forEach(function(n) { childMap[n.id] = []; });
    function canReach(from, to) {
        var queue = [from], seen = {}; seen[from] = true;
        while (queue.length) {
            var cur = queue.shift();
            if (cur === to) return true;
            (childMap[cur] || []).forEach(function(k) { if (!seen[k]) { seen[k] = true; queue.push(k); } });
        }
        return false;
    }
    edges.forEach(function(e) {
        if (childMap[e.source] && nodes.find(function(n) { return n.id === e.target; })) {
            if (hasParent[e.target]) return;
            // Skip if adding this edge would create a cycle (back-edge)
            if (canReach(e.target, e.source)) return;
            if (childMap[e.source].indexOf(e.target) < 0) {
                childMap[e.source].push(e.target);
            }
            hasParent[e.target] = true;
        }
    });

    var roots = nodes.filter(function(n) { return !hasParent[n.id]; }).map(function(n) { return n.id; });
    if (roots.length === 0) roots = [nodes[0].id];

    function subtreeWidth(id, visited) {
        if (!visited) visited = {};
        if (visited[id]) return NODE_W;
        visited[id] = true;
        var kids = childMap[id] || [];
        if (kids.length === 0) return NODE_W;
        var total = kids.reduce(function(sum, kid) { return sum + subtreeWidth(kid, Object.assign({}, visited)) + H_GAP; }, -H_GAP);
        return Math.max(total, NODE_W);
    }

    var positions = {};
    function assign(id, x, y, visited) {
        if (!visited) visited = {};
        if (visited[id]) return;
        visited[id] = true;
        var sw = subtreeWidth(id, {});
        positions[id] = { x: x + sw / 2 - NODE_W / 2, y: y };
        var cx = x;
        var childY = y + nodeH(id) + V_GAP;
        (childMap[id] || []).forEach(function(kid) {
            var ksw = subtreeWidth(kid, {});
            assign(kid, cx, childY, Object.assign({}, visited));
            cx += ksw + H_GAP;
        });
    }

    var rx = 0;
    roots.forEach(function(rootId) {
        assign(rootId, rx, 0, {});
        rx += subtreeWidth(rootId, {}) + H_GAP * 2;
    });

    var maxX = 0, maxY = 0;
    Object.keys(positions).forEach(function(id) {
        if (positions[id].x + NODE_W > maxX) maxX = positions[id].x + NODE_W;
        if (positions[id].y + nodeH(id) > maxY) maxY = positions[id].y + nodeH(id);
    });

    return { positions: positions, width: maxX, height: maxY };
}

// ── SVG edge lines ─────────────────────────────────────────────────────────────
function EdgeLines({ edges, positions, canvasW, canvasH, heights }) {
    var lines = [];
    var h = heights || {};
    edges.forEach(function(e) {
        var sp = positions[e.source];
        var tp = positions[e.target];
        if (!sp || !tp) return;
        if (tp.y <= sp.y) return;
        var srcH = h[e.source] || NODE_H;
        var x1 = sp.x + NODE_W / 2, y1 = sp.y + srcH;
        var x2 = tp.x + NODE_W / 2, y2 = tp.y;
        var midY = (y1 + y2) / 2;
        lines.push({ x1: x1, y1: y1, x2: x2, y2: y2, midY: midY, id: e.id });
    });
    return (
        <svg width={canvasW} height={canvasH} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
            {lines.map(function(l) {
                return (
                    <g key={l.id}>
                        <path
                            d={'M ' + l.x1 + ' ' + l.y1 + ' C ' + l.x1 + ' ' + l.midY + ', ' + l.x2 + ' ' + l.midY + ', ' + l.x2 + ' ' + l.y2}
                            fill="none" stroke="#999" strokeWidth="1.5" strokeOpacity="0.5"
                        />
                        <circle cx={l.x2} cy={l.y2} r="3" fill="#999" opacity="0.5" />
                    </g>
                );
            })}
        </svg>
    );
}

// ── Mini map ───────────────────────────────────────────────────────────────────
var MAP_W = 200, MAP_H = 130;
function MiniMap({ positions, edges, canvasW, canvasH, zoom, panX, panY, containerW, containerH, colorMap, onClickPan }) {
    if (!canvasW || !canvasH) return null;
    var scale = Math.min(MAP_W / canvasW, MAP_H / canvasH) * 0.95;
    var offsetX = (MAP_W - canvasW * scale) / 2;
    var offsetY = (MAP_H - canvasH * scale) / 2;
    // Viewport rect in canvas coords
    var vpX = -panX / zoom;
    var vpY = -panY / zoom;
    var vpW = containerW / zoom;
    var vpH = containerH / zoom;
    var nodeIds = Object.keys(positions);
    function handleClick(e) {
        var rect = e.currentTarget.getBoundingClientRect();
        var mx = e.clientX - rect.left - offsetX;
        var my = e.clientY - rect.top - offsetY;
        var cx = mx / scale;
        var cy = my / scale;
        onClickPan(containerW / 2 - cx * zoom, containerH / 2 - cy * zoom);
    }
    return (
        <div
            onClick={handleClick}
            style={{ position: 'absolute', bottom: 12, right: 12, width: MAP_W, height: MAP_H, background: 'rgba(255,255,255,0.94)', border: '1px solid #d0d7e3', borderRadius: '7px', overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.10)', cursor: 'crosshair', zIndex: 10 }}
        >
            <svg width={MAP_W} height={MAP_H}>
                <g transform={'translate(' + offsetX + ',' + offsetY + ')'}>
                    {edges.map(function(e) {
                        var sp = positions[e.source], tp = positions[e.target];
                        if (!sp || !tp || tp.y <= sp.y) return null;
                        var x1 = (sp.x + NODE_W / 2) * scale, y1 = (sp.y + NODE_H / 2) * scale;
                        var x2 = (tp.x + NODE_W / 2) * scale, y2 = (tp.y + NODE_H / 2) * scale;
                        return <line key={e.id} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#bbb" strokeWidth="0.7" />;
                    })}
                    {nodeIds.map(function(id) {
                        var pos = positions[id];
                        var w = Math.max(NODE_W * scale, 3), h = Math.max(NODE_H * scale, 3);
                        return <rect key={id} x={pos.x * scale} y={pos.y * scale} width={w} height={h} rx="1" fill={colorMap[id] || '#2c3e50'} opacity="0.75" />;
                    })}
                    <rect
                        x={vpX * scale} y={vpY * scale}
                        width={Math.max(vpW * scale, 6)} height={Math.max(vpH * scale, 6)}
                        fill="rgba(41,128,185,0.07)" stroke="rgba(41,128,185,0.55)" strokeWidth="1" rx="2"
                    />
                </g>
            </svg>
            <div style={{ position: 'absolute', bottom: 3, left: 0, right: 0, textAlign: 'center', fontSize: '9px', color: '#aaa', pointerEvents: 'none', letterSpacing: '0.3px' }}>MINIMAP · click to navigate</div>
        </div>
    );
}

// Hook: report rendered height to a parent callback. Runs after every render so
// content-size changes (TTS edited, options added, etc.) propagate to layout/edges.
function useMeasuredHeight(nodeId, onMeasure) {
    var ref = useRef(null);
    useLayoutEffect(function() {
        if (!ref.current || !onMeasure) return;
        var h = ref.current.offsetHeight;
        if (h > 0) onMeasure(nodeId, h);
    });
    return ref;
}

// ── Transfer node card ─────────────────────────────────────────────────────────
function TransferNodeCard({ node, position, selected, onClick, dtmfKey, queuesList, color, onMeasure }) {
    var data = node.data || {};
    color = color || ROOT_COLOR;
    var destType = data.transferType || 'phoneNumber';
    var destLabel;
    if (destType === 'queue') {
        var matched = (queuesList || []).find(function(q) { return q.id === data.transferQueueId; });
        destLabel = matched ? matched.name : (data.transferQueueId ? 'Unknown queue' : 'No queue selected');
    } else {
        destLabel = data.transferPhone || 'No number set';
    }
    var measureRef = useMeasuredHeight(node.id, onMeasure);
    return (
        <div
            ref={measureRef}
            onClick={function(e) { e.stopPropagation(); onClick(node); }}
            onMouseDown={function(e) { e.stopPropagation(); }}
            style={{
                position: 'absolute', left: position.x, top: position.y, width: NODE_W,
                background: '#fff', borderRadius: '8px',
                border: selected ? '2px solid ' + color : '1px solid ' + color + '55',
                boxShadow: selected ? '0 0 0 3px ' + color + '33, 0 2px 12px rgba(0,0,0,0.10)' : '0 2px 8px rgba(0,0,0,0.09)',
                cursor: 'pointer', overflow: 'hidden', userSelect: 'none'
            }}
        >
            <div style={{ background: color, color: '#fff', padding: '9px 12px', fontWeight: '700', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '7px' }}>
                {dtmfKey && <span style={{ opacity: 0.75, fontWeight: '400', flexShrink: 0 }}>[{dtmfKey}]</span>}
                <span style={{ flex: 1 }}>↗ Transfer</span>
            </div>
            <div style={{ padding: '10px 12px' }}>
                <div style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#aaa', marginBottom: '4px' }}>
                    {destType === 'queue' ? 'Queue' : 'Phone Number'}
                </div>
                <div style={{ fontSize: '13px', color: '#333', fontWeight: '500' }}>{destLabel}</div>
            </div>
        </div>
    );
}

// ── Custom block card (light-gray "code" block) ────────────────────────────────
var BLOCK_BG = '#e5e7eb';
var BLOCK_BORDER = '#9ca3af';
var BLOCK_CHIP = '#6b7280';
function CustomBlockCard({ node, position, selected, onClick, dtmfKey, block, allNodes, allFlows, onMeasure }) {
    var data = node.data || {};
    var blockName = (block && block.name) || data.label || data.blockId || 'Custom Block';
    var blockDescription = (block && block.description) || '';
    var missing = !block || !block.id;
    var exits = data.options || [];
    var measureRef = useMeasuredHeight(node.id, onMeasure);
    return (
        <div
            ref={measureRef}
            onClick={function(e) { e.stopPropagation(); onClick(node); }}
            onMouseDown={function(e) { e.stopPropagation(); }}
            style={{
                position: 'absolute', left: position.x, top: position.y, width: NODE_W,
                background: BLOCK_BG, color: '#222', borderRadius: '8px',
                border: selected ? '2px solid ' + BLOCK_CHIP : '1px solid ' + BLOCK_BORDER,
                boxShadow: selected ? '0 0 0 3px ' + BLOCK_CHIP + '55, 0 4px 14px rgba(0,0,0,0.14)' : '0 2px 8px rgba(0,0,0,0.10)',
                cursor: 'pointer', overflow: 'hidden', userSelect: 'none'
            }}
        >
            <div style={{ padding: '10px 12px 8px', display: 'flex', alignItems: 'center', gap: '7px' }}>
                {dtmfKey && <span style={{ background: BLOCK_CHIP, color: '#fff', borderRadius: '3px', padding: '1px 6px', fontSize: '11px', fontWeight: '700', flexShrink: 0 }}>{dtmfKey}</span>}
                <span style={{ flex: 1, fontWeight: '700', fontSize: '13px', color: '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {data.label || blockName}
                </span>
                <span style={{ fontSize: '10px', color: missing ? '#c0392b' : '#6b7280', fontWeight: '700', letterSpacing: '0.5px', textTransform: 'uppercase', flexShrink: 0 }}>
                    {missing ? 'Missing' : 'Block'}
                </span>
            </div>
            {blockDescription && (
                <div style={{ padding: '0 12px 10px', fontSize: '11px', lineHeight: '1.45', color: '#555', maxHeight: '36px', overflow: 'hidden' }}>
                    {blockDescription.length > 110 ? blockDescription.slice(0, 110) + '…' : blockDescription}
                </div>
            )}
            {exits.length > 0 && (
                <div style={{ padding: '6px 12px 10px', borderTop: '1px solid ' + BLOCK_BORDER }}>
                    {exits.slice(0, 3).map(function(opt, i) {
                        return (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: i === 0 ? '4px' : '3px' }}>
                                <span style={{ background: BLOCK_CHIP, color: '#fff', borderRadius: '3px', padding: '1px 6px', fontSize: '10px', fontWeight: '700', flexShrink: 0 }}>
                                    {opt.key || '?'}
                                </span>
                                <span style={{ fontSize: '11px', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#333' }}>
                                    {opt.label || '—'}
                                </span>
                                {opt.action && <span style={{ fontSize: '10px', color: '#666', flexShrink: 0 }}>{opt.action}</span>}
                                {opt.targetFlowId && !opt.action && (function() {
                                    var f = (allFlows || {})[opt.targetFlowId];
                                    var name = (f && f.name) || opt.targetFlowId;
                                    return <span style={{ fontSize: '10px', color: '#666', flexShrink: 0, maxWidth: '90px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>↪ {name}</span>;
                                })()}
                                {opt.targetNodeId && !opt.action && !opt.targetFlowId && (function() {
                                    var target = (allNodes || []).find(function(n) { return n.id === opt.targetNodeId; });
                                    var targetLabel = target && target.data && target.data.label;
                                    return <span style={{ fontSize: '10px', color: '#666', flexShrink: 0, maxWidth: '70px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>→ {targetLabel || ''}</span>;
                                })()}
                            </div>
                        );
                    })}
                    {exits.length > 3 && (
                        <div style={{ fontSize: '10px', color: '#888', marginTop: '3px' }}>+{exits.length - 3} more…</div>
                    )}
                </div>
            )}
        </div>
    );
}

// ── Language picker card ───────────────────────────────────────────────────────
var LANG_PICKER_COLOR = '#002f5f';
function LanguagePickerCard({ node, position, languages, selected, onClick, allNodes, onMeasure }) {
    var data = node.data || {};
    var options = data.options || [];
    var measureRef = useMeasuredHeight(node.id, onMeasure);
    var langByCode = {};
    (languages || []).forEach(function(l) { langByCode[l.code] = l; });
    return (
        <div
            ref={measureRef}
            onClick={function(e) { e.stopPropagation(); onClick(node); }}
            onMouseDown={function(e) { e.stopPropagation(); }}
            style={{
                position: 'absolute', left: position.x, top: position.y, width: NODE_W,
                background: '#fff', borderRadius: '8px',
                border: selected ? '2px solid ' + LANG_PICKER_COLOR : '1px solid ' + LANG_PICKER_COLOR + '55',
                boxShadow: selected ? '0 0 0 3px ' + LANG_PICKER_COLOR + '33, 0 2px 12px rgba(0,0,0,0.10)' : '0 2px 8px rgba(0,0,0,0.09)',
                cursor: 'pointer', overflow: 'hidden', userSelect: 'none'
            }}
        >
            <div style={{ background: LANG_PICKER_COLOR, color: '#fff', padding: '9px 12px', fontWeight: '700', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '7px' }}>
                <span style={{ flex: 1 }}>🌐 {data.label || 'Language Selection'}</span>
            </div>
            <div style={{ padding: '8px 12px', borderBottom: '1px solid #eef0f3' }}>
                <div style={{ fontSize: '11px', color: '#555', lineHeight: '1.45', maxHeight: '42px', overflow: 'hidden' }}>
                    {data.ttsText
                        ? (data.ttsText.length > 75 ? data.ttsText.slice(0, 75) + '…' : data.ttsText)
                        : <em style={{ color: '#bbb' }}>No greeting set</em>}
                </div>
            </div>
            <div style={{ padding: '6px 12px 8px' }}>
                {options.length === 0 && (
                    <div style={{ fontSize: '10px', color: '#bbb', fontStyle: 'italic' }}>No languages — click to edit</div>
                )}
                {options.slice(0, 4).map(function(opt, i) {
                    var lang = langByCode[opt.setsLanguage];
                    var target = opt.targetNodeId ? (allNodes || []).find(function(n) { return n.id === opt.targetNodeId; }) : null;
                    return (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                            <span style={{ background: LANG_PICKER_COLOR, color: '#fff', borderRadius: '3px', padding: '1px 5px', fontSize: '10px', fontWeight: '700', flexShrink: 0 }}>
                                {opt.key || '?'}
                            </span>
                            <span style={{ fontSize: '11px', color: '#444', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {lang ? lang.label : (opt.setsLanguage || '—')}
                            </span>
                            {target && (
                                <span style={{ fontSize: '10px', color: LANG_PICKER_COLOR, flexShrink: 0, maxWidth: '70px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>→ {(target.data || {}).label || ''}</span>
                            )}
                        </div>
                    );
                })}
                {options.length > 4 && (
                    <div style={{ fontSize: '10px', color: '#888', marginTop: '2px' }}>+{options.length - 4} more…</div>
                )}
            </div>
        </div>
    );
}

// ── Entry node card ────────────────────────────────────────────────────────────
// Same general shape as other cards (colored header + white body) but ~15% narrower
// so it reads as a smaller "starting point" cap above the menu graph. Header carries
// an inline phone-with-incoming-arrow SVG. Excluded from `computeColorMap` so it
// doesn't consume a branch color from its sub-menus.
var ENTRY_COLOR = '#002f5f';
var ENTRY_NODE_W = Math.round(NODE_W * 0.82);
function EntryNodeCard({ node, position, selected, onClick, allNodes, allFlows, onMeasure, activeLang, isMultilingual, isDefaultLang, languages }) {
    var data = node.data || {};
    var measureRef = useMeasuredHeight(node.id, onMeasure);
    var ttsPreview = readTts(data, activeLang);
    var missingTranslation = isMultilingual && !nodeHasTranslation(data, activeLang, isDefaultLang);
    var langOptions = Array.isArray(data.languageOptions) ? data.languageOptions : null;
    var next = data.next || {};
    var nextTarget = next.targetNodeId ? (allNodes || []).find(function(n) { return n.id === next.targetNodeId; }) : null;
    var nextFlow = next.targetFlowId ? (allFlows || {})[next.targetFlowId] : null;
    var nextLabel;
    if (next.action) nextLabel = next.action === 'hangup' ? 'Hang Up' : (next.action === 'transfer' ? 'Transfer' : next.action);
    else if (nextFlow) nextLabel = '↪ ' + (nextFlow.name || next.targetFlowId);
    else if (nextTarget) {
        var prefix = nextTarget.type === 'languagePickerNode' ? '🌐 ' : (nextTarget.type === 'transferNode' ? '↗ ' : '→ ');
        nextLabel = prefix + ((nextTarget.data || {}).label || nextTarget.id);
    } else nextLabel = '';

    var langByCode = {};
    (languages || []).forEach(function(l) { langByCode[l.code] = l; });

    // Center the narrower card under the layout slot reserved for it (which is NODE_W wide)
    var leftOffset = position.x + (NODE_W - ENTRY_NODE_W) / 2;

    return (
        <div
            ref={measureRef}
            onClick={function(e) { e.stopPropagation(); onClick(node); }}
            onMouseDown={function(e) { e.stopPropagation(); }}
            style={{
                position: 'absolute', left: leftOffset, top: position.y, width: ENTRY_NODE_W,
                background: '#fff', borderRadius: '8px',
                // Same shape as a regular NodeCard but with no colored header — the
                // whole card is white. Border + shadow mirror NodeCard's look.
                border: selected ? '2px solid ' + ENTRY_COLOR : '1px solid #d8dde5',
                boxShadow: selected ? '0 0 0 3px ' + ENTRY_COLOR + '33, 0 2px 12px rgba(0,0,0,0.10)' : '0 2px 8px rgba(0,0,0,0.09)',
                cursor: 'pointer', overflow: 'hidden', userSelect: 'none'
            }}
        >
            <div style={{ background: '#fff', color: '#333', padding: '8px 11px', fontWeight: '700', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid #eef0f3' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }} aria-hidden="true">
                    <path d="M5 4h3.5l1.6 4-2.1 1.3a11 11 0 0 0 6.7 6.7L16 13.9l4 1.6V19a2 2 0 0 1-2 2A15 15 0 0 1 3 6a2 2 0 0 1 2-2z" fill={ENTRY_COLOR}/>
                    <path d="M19 3l-5 5M14 3h5v5" stroke={ENTRY_COLOR} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                </svg>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{data.label || 'Entry'}</span>
                {missingTranslation && (
                    <span title={'Missing translation for ' + activeLang} style={{ background: '#e67e22', color: '#fff', borderRadius: '8px', padding: '1px 5px', fontSize: '9px', fontWeight: '700', flexShrink: 0 }}>!</span>
                )}
            </div>
            <div style={{ padding: '7px 11px', borderBottom: '1px solid #eef0f3' }}>
                <div style={{ fontSize: '10.5px', color: '#555', lineHeight: '1.4', maxHeight: '36px', overflow: 'hidden' }}>
                    {ttsPreview
                        ? (ttsPreview.length > 70 ? ttsPreview.slice(0, 70) + '…' : ttsPreview)
                        : <em style={{ color: '#bbb' }}>No greeting</em>}
                </div>
            </div>
            {langOptions && (
                <div style={{ padding: '6px 11px 4px', borderBottom: '1px solid #eef0f3' }}>
                    <div style={{ fontSize: '9px', fontWeight: '700', letterSpacing: '0.8px', textTransform: 'uppercase', color: '#aaa', marginBottom: '3px' }}>Languages</div>
                    {langOptions.map(function(opt, i) {
                        var lang = langByCode[opt.setsLanguage];
                        return (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '10.5px', marginBottom: '2px' }}>
                                <span style={{ background: ENTRY_COLOR, color: '#fff', borderRadius: '3px', padding: '1px 5px', fontSize: '10px', fontWeight: '700', flexShrink: 0, minWidth: '14px', textAlign: 'center' }}>{opt.key || '?'}</span>
                                <span style={{ color: '#444' }}>{lang ? lang.label : (opt.setsLanguage || '—')}</span>
                            </div>
                        );
                    })}
                </div>
            )}
            <div style={{ padding: '6px 11px 7px' }}>
                <div style={{ fontSize: '9px', fontWeight: '700', letterSpacing: '0.8px', textTransform: 'uppercase', color: '#aaa', marginBottom: '2px' }}>Next</div>
                <div style={{ fontSize: '10.5px', color: nextLabel ? '#444' : '#bbb', fontStyle: nextLabel ? 'normal' : 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {nextLabel || 'Not set'}
                </div>
            </div>
        </div>
    );
}

// ── Node card ──────────────────────────────────────────────────────────────────
function NodeCard({ node, position, selected, onClick, onAddChild, allNodes, allFlows, color, dtmfKey, descendantCount, collapsed, onToggleCollapse, onMeasure, activeLang, isMultilingual, isDefaultLang }) {
    var data = node.data || {};
    var options = data.options || [];
    color = color || ROOT_COLOR;
    var showCollapseBtn = descendantCount > 5;
    var measureRef = useMeasuredHeight(node.id, onMeasure);
    var ttsPreview = readTts(data, activeLang);
    var missingTranslation = isMultilingual && !nodeHasTranslation(data, activeLang, isDefaultLang);

    return (
        <div
            ref={measureRef}
            onClick={function(e) { e.stopPropagation(); onClick(node); }}
            onMouseDown={function(e) { e.stopPropagation(); }}
            style={{
                position: 'absolute',
                left: position.x,
                top: position.y,
                width: NODE_W,
                background: '#fff',
                borderRadius: '8px',
                border: selected ? '2px solid ' + color : '1px solid ' + color + '55',
                boxShadow: selected ? '0 0 0 3px ' + color + '33, 0 2px 12px rgba(0,0,0,0.10)' : '0 2px 8px rgba(0,0,0,0.09)',
                cursor: 'pointer',
                overflow: 'hidden',
                userSelect: 'none'
            }}
        >
            <div style={{ background: color, color: '#fff', padding: '9px 12px', fontWeight: '700', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '7px' }}>
                {dtmfKey && <span style={{ opacity: 0.75, fontWeight: '400', flexShrink: 0 }}>[{dtmfKey}]</span>}
                <span style={{ flex: 1 }}>{data.label || 'Node'}</span>
                {missingTranslation && (
                    <span title={'Missing translation for ' + activeLang} style={{ background: '#e67e22', color: '#fff', borderRadius: '8px', padding: '1px 6px', fontSize: '9px', fontWeight: '700', letterSpacing: '0.4px', flexShrink: 0 }}>!</span>
                )}
            </div>
            {!collapsed && (
                <div style={{ padding: '8px 12px', borderBottom: '1px solid #eef0f3' }}>
                    <div style={{ fontSize: '11px', color: '#555', lineHeight: '1.45', maxHeight: '42px', overflow: 'hidden' }}>
                        {ttsPreview
                            ? (ttsPreview.length > 75 ? ttsPreview.slice(0, 75) + '…' : ttsPreview)
                            : <em style={{ color: '#bbb' }}>No TTS text</em>}
                    </div>
                </div>
            )}
            {!collapsed && (
                <div style={{ padding: '6px 12px 8px' }}>
                    {options.length === 0 && (
                        <div style={{ fontSize: '10px', color: '#bbb', fontStyle: 'italic' }}>No options — click to edit</div>
                    )}
                    {options.slice(0, 3).map(function(opt, i) {
                        var hasTarget = !!opt.targetNodeId || !!opt.action || !!opt.targetFlowId;
                        return (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                                <span style={{ background: color, color: '#fff', borderRadius: '3px', padding: '1px 5px', fontSize: '10px', fontWeight: '700', flexShrink: 0 }}>
                                    {opt.key || '?'}
                                </span>
                                <span style={{ fontSize: '11px', color: '#444', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {opt.label || '—'}
                                </span>
                                {opt.action && <span style={{ fontSize: '10px', color: '#888', flexShrink: 0 }}>{opt.action}</span>}
                                {opt.targetFlowId && !opt.action && (function() {
                                    var f = (allFlows || {})[opt.targetFlowId];
                                    var name = (f && f.name) || opt.targetFlowId;
                                    return <span style={{ fontSize: '10px', color: color, flexShrink: 0, maxWidth: '90px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>↪ {name}</span>;
                                })()}
                                {opt.targetNodeId && !opt.action && !opt.targetFlowId && (function() {
                                    var target = allNodes.find(function(n) { return n.id === opt.targetNodeId; });
                                    var targetLabel = target && target.data && target.data.label;
                                    return <span style={{ fontSize: '10px', color: color, flexShrink: 0, maxWidth: '70px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>→ {targetLabel || ''}</span>;
                                })()}
                                {!hasTarget && onAddChild && (
                                    <button
                                        onClick={function(e) { e.stopPropagation(); onAddChild(node, i); }}
                                        style={{ fontSize: '10px', background: 'none', border: '1px dashed ' + color, borderRadius: '3px', color: color, padding: '1px 5px', cursor: 'pointer', flexShrink: 0 }}
                                    >+</button>
                                )}
                            </div>
                        );
                    })}
                    {options.length > 3 && (
                        <div style={{ fontSize: '10px', color: '#888', marginTop: '2px' }}>+{options.length - 3} more…</div>
                    )}
                </div>
            )}
            {showCollapseBtn && (
                <button
                    onClick={function(e) { onToggleCollapse(node.id, e); }}
                    style={{
                        display: 'block', width: '100%', padding: '5px 12px',
                        background: collapsed ? color + '18' : 'transparent',
                        border: 'none', borderTop: '1px solid ' + color + '33',
                        color: color, fontSize: '10px', fontWeight: '600',
                        cursor: 'pointer', textAlign: 'center', letterSpacing: '0.5px'
                    }}
                >
                    {collapsed ? '▶  Show ' + descendantCount + ' nodes' : '▲  Collapse subtree (' + descendantCount + ' nodes)'}
                </button>
            )}
        </div>
    );
}

// ── Helpers ────────────────────────────────────────────────────────────────────
function newNodeId() { return 'node_' + Math.random().toString(36).slice(2, 9); }

// Phone-keypad order: 1,2,3,4,5,6,7,8,9,*,0,#
var KEYPAD_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];
function dtmfSortValue(k) {
    var i = KEYPAD_KEYS.indexOf(String(k));
    return i === -1 ? 999 : i;
}

// ── Language helpers ───────────────────────────────────────────────────────────
var DEFAULT_LANG = 'en-US';
var COMMON_LANGUAGES = [
    { code: 'en-US', label: 'English (US)' },
    { code: 'es-US', label: 'Spanish (US)' },
    { code: 'es-MX', label: 'Spanish (Mexico)' },
    { code: 'fr-FR', label: 'French' },
    { code: 'fr-CA', label: 'French (Canada)' },
    { code: 'de-DE', label: 'German' },
    { code: 'it-IT', label: 'Italian' },
    { code: 'pt-BR', label: 'Portuguese (Brazil)' },
    { code: 'zh-CN', label: 'Chinese (Mandarin)' },
    { code: 'ja-JP', label: 'Japanese' },
    { code: 'ko-KR', label: 'Korean' }
];

function flowLanguages(flow) {
    var langs = flow && flow.languages;
    if (Array.isArray(langs) && langs.length > 0) return langs;
    return [{ code: DEFAULT_LANG, label: 'English (US)', isDefault: true }];
}
function flowDefaultLang(flow) {
    var langs = flowLanguages(flow);
    var def = langs.find(function(l) { return l.isDefault; });
    return def ? def.code : langs[0].code;
}
// Read TTS text for a node, falling back to legacy `ttsText` (pre-multilingual)
function readTts(data, lang) {
    if (!data) return '';
    if (data.ttsTextByLang && data.ttsTextByLang[lang] != null) return data.ttsTextByLang[lang];
    return data.ttsText || '';
}
function readPrompt(data, lang) {
    if (!data) return '';
    if (data.promptByLang && data.promptByLang[lang] != null) return data.promptByLang[lang];
    return data.prompt || '';
}
function readAlternatives(opt, lang) {
    if (!opt) return [];
    if (opt.voiceAlternativesByLang && Array.isArray(opt.voiceAlternativesByLang[lang])) return opt.voiceAlternativesByLang[lang];
    return opt.voiceAlternatives || [];
}
// Idempotently ensures a flow's nodes start with an entryNode. Old flows (saved
// before the entry-block pattern) get one prepended, wired to the previous root.
// Pure — returns a new array if a node was added, otherwise the original reference.
function ensureEntryNode(nodes) {
    if (!Array.isArray(nodes) || nodes.length === 0) return nodes;
    if (nodes.some(function(n) { return n.type === 'entryNode'; })) return nodes;
    // Find the existing root: a node with no incoming references from any other
    // node's options[].targetNodeId or next.targetNodeId.
    var hasIncoming = {};
    nodes.forEach(function(n) {
        var data = n.data || {};
        ((data.options) || []).forEach(function(opt) {
            if (opt.targetNodeId) hasIncoming[opt.targetNodeId] = true;
        });
        if (data.next && data.next.targetNodeId) hasIncoming[data.next.targetNodeId] = true;
    });
    var root = nodes.find(function(n) { return !hasIncoming[n.id]; }) || nodes[0];
    var entryId = 'node_' + Math.random().toString(36).slice(2, 9);
    var entry = {
        id: entryId, type: 'entryNode', position: { x: 0, y: 0 },
        data: { label: 'Entry', ttsTextByLang: {}, next: { targetNodeId: root.id } }
    };
    return [entry].concat(nodes);
}

// True if this node has any per-language content for the active lang. Used for the
// missing-translation badge — only meaningful when the flow has more than one language.
function nodeHasTranslation(data, lang, isDefault) {
    if (!data) return true;
    var hasTts = data.ttsTextByLang && data.ttsTextByLang[lang] && data.ttsTextByLang[lang].trim();
    var hasPrompt = data.promptByLang && data.promptByLang[lang];
    if (hasTts || hasPrompt) return true;
    // Legacy flat content counts only as the default-language translation
    if (isDefault && ((data.ttsText && data.ttsText.trim()) || data.prompt)) return true;
    return false;
}

// Compute tree-style ancestors of a node, skipping back-edges (e.g., a child option
// that "returns to parent"). Mirrors the cycle-safe edge selection in buildLayout.
function computeTreeAncestors(nodeId, nodes, edges) {
    var childMap = {};
    var hasParent = {};
    var treeParent = {};
    nodes.forEach(function(n) { childMap[n.id] = []; });
    function canReach(from, to) {
        var q = [from], seen = {}; seen[from] = true;
        while (q.length) {
            var cur = q.shift();
            if (cur === to) return true;
            (childMap[cur] || []).forEach(function(k) { if (!seen[k]) { seen[k] = true; q.push(k); } });
        }
        return false;
    }
    edges.forEach(function(e) {
        if (!childMap[e.source]) return;
        if (hasParent[e.target]) return;
        if (canReach(e.target, e.source)) return;
        if (childMap[e.source].indexOf(e.target) < 0) childMap[e.source].push(e.target);
        hasParent[e.target] = true;
        treeParent[e.target] = e.source;
    });
    var ancestorIds = {};
    var cur = nodeId;
    while (treeParent[cur] && !ancestorIds[treeParent[cur]]) {
        cur = treeParent[cur];
        ancestorIds[cur] = true;
    }
    return ancestorIds;
}

var labelStyle = {
    display: 'block', fontSize: '11px', fontWeight: '700', letterSpacing: '1px',
    textTransform: 'uppercase', color: '#888', marginBottom: '8px'
};
var inputStyle = {
    width: '100%', padding: '10px 12px', border: '1px solid #ccc', borderRadius: '6px',
    fontSize: '14px', color: '#222', background: '#fff', boxSizing: 'border-box', marginBottom: '20px'
};

// ── Main class ─────────────────────────────────────────────────────────────────
class IVRFlows extends Component {
    constructor(props) {
        super(props);
        this._containerRef = null;
        this._canvasRef = null;
        this._drag = null;
        this._dragMoved = false;
        this._pendingPan = null;
        this.state = {
            activeFlowId: '',
            nodes: [],
            edges: [],
            languages: [{ code: DEFAULT_LANG, label: 'English (US)', isDefault: true }],
            activeLang: DEFAULT_LANG,
            selectedNodeId: null,
            zoom: 1,
            panX: 40,
            panY: 40,
            isDirty: false,
            editingNode: null,
            flowMenuOpen: false,
            addingFlow: false,
            renamingFlow: false,
            deletingFlow: false,
            managingLanguages: false,
            newFlowName: '',
            loading: false,
            error: '',
            confirmingNodeDelete: false,
            collapsedNodes: {},
            editingAlternativesIdx: null,
            nodeHeights: {},
            optionTypeHints: {},
            keypadOpenIdx: null
        };
    }

    componentDidMount() {
        var flows = ((this.props.data.IVRFlows || {}).data) || {};
        var keys = Object.keys(flows);
        if (keys.length > 0) {
            var flow = flows[keys[0]];
            var langs = flowLanguages(flow);
            this.setState({ activeFlowId: keys[0], nodes: ensureEntryNode(flow.nodes || []), edges: flow.edges || [], languages: langs, activeLang: flowDefaultLang(flow) });
            this._pendingCenter = true;
        }
    }

    // Recenters the canvas so the flow's entry node sits at the top-center of the
    // viewport. Called after first mount and after switching flows. Deferred via
    // requestAnimationFrame so the container ref + node heights are measured.
    centerOnEntry = () => {
        var run = function() {
            if (!this._containerRef) return;
            var entry = this.state.nodes.find(function(n) { return n.type === 'entryNode'; });
            if (!entry) return;
            // Recompute layout using the same edge derivation render() uses
            var derivedEdges = [];
            this.state.nodes.forEach(function(n) {
                if (n.type === 'entryNode') {
                    var nx = (n.data || {}).next || {};
                    if (nx.targetNodeId) derivedEdges.push({ id: 'e_' + n.id + '_next', source: n.id, target: nx.targetNodeId });
                    return;
                }
                ((n.data || {}).options || []).forEach(function(opt) {
                    if (opt.targetNodeId) derivedEdges.push({ id: 'e_' + n.id + '_' + (opt.key || opt.targetNodeId), source: n.id, target: opt.targetNodeId });
                });
            });
            var layout = buildLayout(this.state.nodes, derivedEdges, this.state.nodeHeights);
            var pos = layout.positions[entry.id];
            if (!pos) return;
            var containerW = this._containerRef.clientWidth;
            var zoom = this.state.zoom;
            var newPanX = containerW / 2 - (pos.x + NODE_W / 2) * zoom;
            this.setState({ panX: newPanX, panY: 40 });
        }.bind(this);
        // Two rAFs: first lets the canvas mount; second waits for measured heights
        // to flow back through componentDidUpdate so the layout is final.
        requestAnimationFrame(function() { requestAnimationFrame(run); });
    }

    componentDidUpdate(prevProps) {
        // Attach wheel listener as non-passive so preventDefault works
        if (this._containerRef && !this._containerRef._wheelAttached) {
            this._containerRef._wheelAttached = true;
            this._containerRef.addEventListener('wheel', this.onCanvasWheel, { passive: false });
        }
        // Re-center when the view is opened — the canvas isn't mounted while
        // inactive, so the original mount-time centering had nothing to measure.
        if (prevProps && !prevProps.active && this.props.active) {
            this._pendingCenter = true;
        }
        if (this._pendingCenter) {
            this._pendingCenter = false;
            this.centerOnEntry();
        }
    }

    componentWillUnmount() {
        if (this._containerRef) {
            this._containerRef.removeEventListener('wheel', this.onCanvasWheel);
        }
    }

    componentWillReceiveProps(nextProps) {
        if (this.state.loading) {
            this.setState({ loading: false, isDirty: false });
        }
        var flows = ((nextProps.data.IVRFlows || {}).data) || {};
        var activeId = this.state.activeFlowId;
        if (!flows[activeId]) {
            var keys = Object.keys(flows);
            if (keys.length > 0) {
                var flow = flows[keys[0]];
                var langs = flowLanguages(flow);
                this.setState({ activeFlowId: keys[0], nodes: ensureEntryNode(flow.nodes || []), edges: flow.edges || [], languages: langs, activeLang: flowDefaultLang(flow), isDirty: false });
                this._pendingCenter = true;
            } else {
                this.setState({ activeFlowId: '', nodes: [], edges: [], languages: [{ code: DEFAULT_LANG, label: 'English (US)', isDefault: true }], activeLang: DEFAULT_LANG, isDirty: false });
            }
            return;
        }
        if (!this.state.isDirty && flows[activeId]) {
            var flow = flows[activeId];
            var langs = flowLanguages(flow);
            // Preserve activeLang if still in the language set; else fall back to default
            var stillActive = langs.find(function(l) { return l.code === this.state.activeLang; }.bind(this));
            this.setState({ nodes: ensureEntryNode(flow.nodes || []), edges: flow.edges || [], languages: langs, activeLang: stillActive ? this.state.activeLang : flowDefaultLang(flow) });
        }
    }

    switchFlow = (flowId) => {
        var flows = ((this.props.data.IVRFlows || {}).data) || {};
        var flow = flows[flowId];
        if (!flow) return;
        var langs = flowLanguages(flow);
        this.setState({ activeFlowId: flowId, nodes: ensureEntryNode(flow.nodes || []), edges: flow.edges || [], languages: langs, activeLang: flowDefaultLang(flow), isDirty: false, selectedNodeId: null });
        this._pendingCenter = true;
    }

    // ── Node editing ───────────────────────────────────────────────────────────
    openEditNode = (node) => {
        this.setState({ editingNode: JSON.parse(JSON.stringify(node)), selectedNodeId: node.id });
    }

    saveNode = () => {
        var editingNode = this.state.editingNode;
        // Strip transient UI hints (e.g. _nextTypeHint) before persisting
        var cleanData = JSON.parse(JSON.stringify(editingNode.data));
        this.setState(function(s) {
            var nodes = s.nodes.map(function(n) {
                return n.id === editingNode.id ? Object.assign({}, n, { data: cleanData }) : n;
            });
            // Rebuild edges originating from this node — entry nodes use `data.next`,
            // everything else uses `data.options[].targetNodeId`.
            var autoEdges = [];
            if (editingNode.type === 'entryNode') {
                var next = cleanData.next || {};
                if (next.targetNodeId) {
                    autoEdges.push({ id: 'e_' + editingNode.id + '_next', source: editingNode.id, target: next.targetNodeId });
                }
            } else {
                (cleanData.options || []).forEach(function(opt) {
                    if (opt.targetNodeId) {
                        autoEdges.push({ id: 'e_' + editingNode.id + '_' + opt.key, source: editingNode.id, target: opt.targetNodeId, label: opt.key });
                    }
                });
            }
            var otherEdges = s.edges.filter(function(e) { return e.source !== editingNode.id; });
            return { nodes: nodes, edges: otherEdges.concat(autoEdges), editingNode: null, isDirty: true, optionTypeHints: {}, keypadOpenIdx: null };
        });
    }

    editNodeField = (field) => (e) => {
        var val = e.target.value;
        this.setState(function(s) {
            var node = JSON.parse(JSON.stringify(s.editingNode));
            node.data[field] = val;
            return { editingNode: node };
        });
    }

    setNodeAudioMode = (mode) => {
        this.setState(function(s) {
            var node = JSON.parse(JSON.stringify(s.editingNode));
            node.data.audioMode = mode;
            return { editingNode: node };
        });
    }

    // Migrates a node's legacy flat fields (ttsText, prompt, options[].voiceAlternatives)
    // into the per-language structure under the flow's default language, so writes to
    // a non-default language don't silently drop the legacy content. Mutates `node`
    // in place. Idempotent — once the legacy field is gone, this is a no-op.
    migrateLegacyFields = (node) => {
        if (node.type === 'languagePickerNode') return;
        var defaultLang = flowDefaultLang({ languages: this.state.languages });
        var data = node.data || {};
        if (data.ttsText != null) {
            if (!data.ttsTextByLang) data.ttsTextByLang = {};
            if (data.ttsTextByLang[defaultLang] == null) data.ttsTextByLang[defaultLang] = data.ttsText;
            delete data.ttsText;
        }
        if (data.prompt != null) {
            if (!data.promptByLang) data.promptByLang = {};
            if (data.promptByLang[defaultLang] == null) data.promptByLang[defaultLang] = data.prompt;
            delete data.prompt;
        }
        (data.options || []).forEach(function(opt) {
            if (Array.isArray(opt.voiceAlternatives) && opt.voiceAlternatives.length) {
                if (!opt.voiceAlternativesByLang) opt.voiceAlternativesByLang = {};
                if (!Array.isArray(opt.voiceAlternativesByLang[defaultLang])) {
                    opt.voiceAlternativesByLang[defaultLang] = opt.voiceAlternatives.slice();
                }
                delete opt.voiceAlternatives;
            }
        });
    }

    editNodeTts = (e) => {
        var val = e.target.value;
        var lang = this.state.activeLang;
        this.setState(function(s) {
            var node = JSON.parse(JSON.stringify(s.editingNode));
            this.migrateLegacyFields(node);
            if (!node.data.ttsTextByLang) node.data.ttsTextByLang = {};
            node.data.ttsTextByLang[lang] = val;
            return { editingNode: node };
        }.bind(this));
    }

    setNodePrompt = (value) => {
        var lang = this.state.activeLang;
        this.setState(function(s) {
            var node = JSON.parse(JSON.stringify(s.editingNode));
            // Language-picker nodes play before any language is chosen, so their
            // audio is a single shared (bilingual) string, not per-language.
            if (node.type === 'languagePickerNode') {
                node.data.prompt = value;
            } else {
                this.migrateLegacyFields(node);
                if (!node.data.promptByLang) node.data.promptByLang = {};
                node.data.promptByLang[lang] = value;
            }
            return { editingNode: node };
        }.bind(this));
    }

    editNodeOption = (idx, field, val) => {
        this.setState(function(s) {
            var node = JSON.parse(JSON.stringify(s.editingNode));
            node.data.options[idx][field] = val;
            return { editingNode: node };
        });
    }

    // Returns the live alternatives array for an option, mutating the option in
    // place to ensure it exists. Picker-node options use the flat `voiceAlternatives`
    // (since the caller hasn't selected a language yet); regular options use the
    // per-language structure. On first edit of a per-language array, seeds it from
    // the legacy `voiceAlternatives` field so existing data isn't lost.
    altsArrayForEdit = (node, option, lang) => {
        if (node.type === 'languagePickerNode') {
            if (!Array.isArray(option.voiceAlternatives)) option.voiceAlternatives = [];
            return option.voiceAlternatives;
        }
        // Migrate any legacy alternatives on this node onto the default lang first,
        // then ensure the editing lang's array exists (fresh, not seeded from legacy).
        this.migrateLegacyFields(node);
        if (!option.voiceAlternativesByLang) option.voiceAlternativesByLang = {};
        if (!Array.isArray(option.voiceAlternativesByLang[lang])) {
            option.voiceAlternativesByLang[lang] = [];
        }
        return option.voiceAlternativesByLang[lang];
    }

    addAlternative = (optIdx) => {
        var newAltIdx;
        var lang = this.state.activeLang;
        this.setState(function(s) {
            var node = JSON.parse(JSON.stringify(s.editingNode));
            var arr = this.altsArrayForEdit(node, node.data.options[optIdx], lang);
            arr.push('');
            newAltIdx = arr.length - 1;
            return { editingNode: node };
        }.bind(this), function() {
            this.focusAlternative(optIdx, newAltIdx);
        }.bind(this));
    }

    insertAlternativeAfter = (optIdx, altIdx) => {
        var newAltIdx = altIdx + 1;
        var lang = this.state.activeLang;
        this.setState(function(s) {
            var node = JSON.parse(JSON.stringify(s.editingNode));
            this.altsArrayForEdit(node, node.data.options[optIdx], lang).splice(newAltIdx, 0, '');
            return { editingNode: node };
        }.bind(this), function() {
            this.focusAlternative(optIdx, newAltIdx);
        }.bind(this));
    }

    focusAlternative = (optIdx, altIdx) => {
        setTimeout(function() {
            var el = document.querySelector('input[data-alt="' + optIdx + '_' + altIdx + '"]');
            if (el) el.focus();
        }, 0);
    }

    editAlternative = (optIdx, altIdx, val) => {
        var lang = this.state.activeLang;
        this.setState(function(s) {
            var node = JSON.parse(JSON.stringify(s.editingNode));
            this.altsArrayForEdit(node, node.data.options[optIdx], lang)[altIdx] = val;
            return { editingNode: node };
        }.bind(this));
    }

    removeAlternative = (optIdx, altIdx) => {
        var lang = this.state.activeLang;
        this.setState(function(s) {
            var node = JSON.parse(JSON.stringify(s.editingNode));
            this.altsArrayForEdit(node, node.data.options[optIdx], lang).splice(altIdx, 1);
            return { editingNode: node };
        }.bind(this));
    }

    // ── Entry node "next" handlers ─────────────────────────────────────────────
    setEntryNextType = (type) => {
        // Resets data.next when the user changes the next-target *type* in the picker.
        // Keeps a transient hint on the editing node so the UI knows which sub-picker
        // to show before any concrete target/action has been chosen for that type.
        this.setState(function(s) {
            var node = JSON.parse(JSON.stringify(s.editingNode));
            node.data.next = {};
            node._nextTypeHint = type;
            return { editingNode: node };
        });
    }

    setEntryNextTarget = (value) => {
        // Mirrors editNodeOptionTarget for the single-target entry node.
        if (value === 'action:transfer') { this.createTransferChildForEntry(); return; }
        if (value === 'action:languageSelection') { this.createLanguagePickerForEntry(); return; }
        if (value === 'newMenu:default') { this.createMenuChildForEntry(); return; }
        if (value && value.startsWith('newBlock:')) { this.createCustomBlockChildForEntry(value.replace('newBlock:', '')); return; }
        if (value && value.startsWith('flow:')) {
            var flowId = value.replace('flow:', '');
            this.setState(function(s) {
                var node = JSON.parse(JSON.stringify(s.editingNode));
                node.data.next = { targetFlowId: flowId };
                return { editingNode: node };
            });
            return;
        }
        if (value && value.startsWith('action:')) {
            var action = value.replace('action:', '');
            this.setState(function(s) {
                var node = JSON.parse(JSON.stringify(s.editingNode));
                node.data.next = { action: action };
                return { editingNode: node };
            });
            return;
        }
        this.setState(function(s) {
            var node = JSON.parse(JSON.stringify(s.editingNode));
            node.data.next = value ? { targetNodeId: value } : {};
            return { editingNode: node };
        });
    }

    // Helpers that create a new child and wire entry.next to it. Mirrors the
    // create-child flow used by ivrNode options.
    _commitEntryWithChild = (newNode) => {
        var updated = JSON.parse(JSON.stringify(this.state.editingNode));
        updated.data.next = { targetNodeId: newNode.id };
        delete updated._nextTypeHint;
        this.setState(function(s) {
            var nodes = s.nodes.map(function(n) {
                return n.id === updated.id ? Object.assign({}, n, { data: updated.data }) : n;
            }).concat([newNode]);
            return { nodes: nodes, isDirty: true, editingNode: updated, selectedNodeId: updated.id };
        });
    }

    createMenuChildForEntry = () => {
        this._commitEntryWithChild({
            id: newNodeId(), type: 'ivrNode', position: { x: 0, y: 0 },
            data: { label: 'Menu', ttsTextByLang: {}, options: [], noInput: { action: 'repeat' }, noMatch: { action: 'repeat' } }
        });
    }

    createTransferChildForEntry = () => {
        this._commitEntryWithChild({
            id: newNodeId(), type: 'transferNode', position: { x: 0, y: 0 },
            data: { label: 'Transfer', transferType: 'phoneNumber', transferPhone: '', transferQueueId: '' }
        });
    }

    createCustomBlockChildForEntry = (blockId) => {
        var settings = ((this.props.data.IVRFlows || {}).settings) || {};
        var block = (settings.customBlocks || {})[blockId];
        if (!block) { this.setState({ error: 'Custom block "' + blockId + '" is not configured for this domain.' }); return; }
        this._commitEntryWithChild({
            id: newNodeId(), type: 'customNode', position: { x: 0, y: 0 },
            data: {
                blockId: blockId, label: block.name || blockId,
                options: (block.exits || []).map(function(ex) { return { key: ex.key, label: ex.label, targetNodeId: '' }; })
            }
        });
    }

    // ── Entry languageOptions handlers (multilingual entry block) ──────────────
    setEntryLangOptionTarget = (idx, value) => {
        // Mirrors editNodeOptionTarget but writes into entry.data.languageOptions[idx].
        // Handles "newMenu", "newBlock:<id>", "action:transfer", "action:hangup",
        // "flow:<id>", or a node id.
        if (value === 'action:transfer') { this._createTransferChildForEntryLangOption(idx); return; }
        if (value === 'newMenu:default') { this._createMenuChildForEntryLangOption(idx); return; }
        if (value && value.startsWith('newBlock:')) { this._createCustomBlockChildForEntryLangOption(idx, value.replace('newBlock:', '')); return; }
        if (value && value.startsWith('flow:')) {
            var flowId = value.replace('flow:', '');
            this.setState(function(s) {
                var node = JSON.parse(JSON.stringify(s.editingNode));
                var opt = node.data.languageOptions[idx];
                delete opt.targetNodeId; delete opt.action;
                opt.targetFlowId = flowId;
                return { editingNode: node };
            });
            return;
        }
        if (value && value.startsWith('action:')) {
            var action = value.replace('action:', '');
            this.setState(function(s) {
                var node = JSON.parse(JSON.stringify(s.editingNode));
                var opt = node.data.languageOptions[idx];
                delete opt.targetNodeId; delete opt.targetFlowId;
                opt.action = action;
                return { editingNode: node };
            });
            return;
        }
        this.setState(function(s) {
            var node = JSON.parse(JSON.stringify(s.editingNode));
            var opt = node.data.languageOptions[idx];
            delete opt.action; delete opt.targetFlowId;
            opt.targetNodeId = value || '';
            return { editingNode: node };
        });
    }

    setEntryLangOptionType = (idx, newType) => {
        // Resets the option's destination when the user changes type, and stores a
        // hint so the right sub-picker shows before a concrete target is chosen.
        this.setState(function(s) {
            var node = JSON.parse(JSON.stringify(s.editingNode));
            var opt = node.data.languageOptions[idx];
            if (opt) {
                delete opt.action; delete opt.targetFlowId;
                opt.targetNodeId = '';
            }
            var hints = Object.assign({}, s.optionTypeHints);
            hints[idx] = newType;
            return { editingNode: node, optionTypeHints: hints };
        });
    }

    setEntryLangOptionKey = (idx, newKey) => {
        this.setState(function(s) {
            var node = JSON.parse(JSON.stringify(s.editingNode));
            if (!node.data.languageOptions || !node.data.languageOptions[idx]) return {};
            node.data.languageOptions[idx].key = newKey;
            node.data.languageOptions.sort(function(a, b) { return dtmfSortValue(a.key) - dtmfSortValue(b.key); });
            return { editingNode: node, keypadOpenIdx: null, optionTypeHints: {}, editingAlternativesIdx: null };
        });
    }

    _createChildForEntryLangOption = (idx, newNode) => {
        var updated = JSON.parse(JSON.stringify(this.state.editingNode));
        var opt = updated.data.languageOptions[idx];
        delete opt.action; delete opt.targetFlowId;
        opt.targetNodeId = newNode.id;
        this.setState(function(s) {
            var nodes = s.nodes.map(function(n) {
                return n.id === updated.id ? Object.assign({}, n, { data: updated.data }) : n;
            }).concat([newNode]);
            return { nodes: nodes, isDirty: true, editingNode: updated, selectedNodeId: updated.id };
        });
    }

    _createMenuChildForEntryLangOption = (idx) => {
        this._createChildForEntryLangOption(idx, {
            id: newNodeId(), type: 'ivrNode', position: { x: 0, y: 0 },
            data: { label: 'Menu', ttsTextByLang: {}, options: [], noInput: { action: 'repeat' }, noMatch: { action: 'repeat' } }
        });
    }

    _createTransferChildForEntryLangOption = (idx) => {
        this._createChildForEntryLangOption(idx, {
            id: newNodeId(), type: 'transferNode', position: { x: 0, y: 0 },
            data: { label: 'Transfer', transferType: 'phoneNumber', transferPhone: '', transferQueueId: '' }
        });
    }

    _createCustomBlockChildForEntryLangOption = (idx, blockId) => {
        var settings = ((this.props.data.IVRFlows || {}).settings) || {};
        var block = (settings.customBlocks || {})[blockId];
        if (!block) { this.setState({ error: 'Custom block "' + blockId + '" is not configured for this domain.' }); return; }
        this._createChildForEntryLangOption(idx, {
            id: newNodeId(), type: 'customNode', position: { x: 0, y: 0 },
            data: {
                blockId: blockId, label: block.name || blockId,
                options: (block.exits || []).map(function(ex) { return { key: ex.key, label: ex.label, targetNodeId: '' }; })
            }
        });
    }

    // Builds a Language Picker node with one option per configured language and
    // wires the entry to it. The default language's option points to whatever
    // entry.next was previously, so the existing flow remains reachable.
    createLanguagePickerForEntry = () => {
        var langs = this.state.languages || [];
        var prevTarget = ((this.state.editingNode.data || {}).next || {}).targetNodeId || '';
        var defaultLang = flowDefaultLang({ languages: langs });
        var pickerOptions = langs.map(function(l, i) {
            return {
                key: String(i + 1),
                label: l.label,
                setsLanguage: l.code,
                targetNodeId: l.code === defaultLang ? prevTarget : '',
                voiceAlternatives: []
            };
        });
        this._commitEntryWithChild({
            id: newNodeId(), type: 'languagePickerNode', position: { x: 0, y: 0 },
            data: {
                label: 'Language Selection',
                ttsText: 'For English, press 1.',
                options: pickerOptions,
                noInput: { action: 'repeat' }, noMatch: { action: 'repeat' }
            }
        });
    }

    // Picker-only: assign which language an option selects + (optionally) the node
    // to advance to. We default the target to the first non-picker node so the call
    // doesn't dead-end when the user just picks a language and saves.
    editPickerOptionLang = (idx, code) => {
        this.setState(function(s) {
            var node = JSON.parse(JSON.stringify(s.editingNode));
            var opt = node.data.options[idx];
            if (opt) {
                opt.setsLanguage = code;
                var langs = s.languages || [];
                var langObj = langs.find(function(l) { return l.code === code; });
                if (langObj && (!opt.label || opt.label === langObj.code)) opt.label = langObj.label;
            }
            return { editingNode: node };
        });
    }

    editPickerOptionTarget = (idx, nodeId) => {
        this.setState(function(s) {
            var node = JSON.parse(JSON.stringify(s.editingNode));
            if (node.data.options[idx]) node.data.options[idx].targetNodeId = nodeId;
            return { editingNode: node };
        });
    }

    editNodeOptionTarget = (idx, value) => {
        if (value === 'action:transfer') {
            this.createTransferChildForOption(idx);
            return;
        }
        if (value === 'newMenu:default') {
            this.createChildNodeForOption(idx);
            return;
        }
        if (value && value.startsWith('newBlock:')) {
            this.createCustomBlockChildForOption(idx, value.replace('newBlock:', ''));
            return;
        }
        if (value && value.startsWith('flow:')) {
            var flowId = value.replace('flow:', '');
            this.setState(function(s) {
                var node = JSON.parse(JSON.stringify(s.editingNode));
                delete node.data.options[idx].action;
                node.data.options[idx].targetNodeId = '';
                node.data.options[idx].targetFlowId = flowId;
                return { editingNode: node };
            });
            return;
        }
        this.setState(function(s) {
            var node = JSON.parse(JSON.stringify(s.editingNode));
            delete node.data.options[idx].targetFlowId;
            if (value.startsWith('action:')) {
                node.data.options[idx].action = value.replace('action:', '');
                node.data.options[idx].targetNodeId = '';
            } else {
                node.data.options[idx].targetNodeId = value;
                delete node.data.options[idx].action;
            }
            return { editingNode: node };
        });
    }

    addOption = () => {
        this.setState(function(s) {
            var node = JSON.parse(JSON.stringify(s.editingNode));
            if (!node.data.options) node.data.options = [];
            node.data.options.push({ key: String(node.data.options.length + 1), label: '', targetNodeId: '', voiceAlternativesByLang: {} });
            return { editingNode: node };
        });
    }

    removeOption = (idx) => {
        this.setState(function(s) {
            var node = JSON.parse(JSON.stringify(s.editingNode));
            node.data.options.splice(idx, 1);
            return { editingNode: node };
        });
    }

    editFallback = (field, val) => {
        this.setState(function(s) {
            var node = JSON.parse(JSON.stringify(s.editingNode));
            if (!node.data[field]) node.data[field] = {};
            node.data[field].action = val;
            return { editingNode: node };
        });
    }

    editTransferNodeField = (field) => (e) => {
        var val = e.target.value;
        this.setState(function(s) {
            var node = JSON.parse(JSON.stringify(s.editingNode));
            node.data[field] = val;
            return { editingNode: node };
        });
    }

    createTransferChildForOption = (idx) => {
        var newId = newNodeId();
        var transferNode = {
            id: newId, type: 'transferNode', position: { x: 0, y: 0 },
            data: { label: 'Transfer', transferType: 'phoneNumber', transferPhone: '', transferQueueId: '' }
        };
        var updatedEditingNode = JSON.parse(JSON.stringify(this.state.editingNode));
        updatedEditingNode.data.options[idx].targetNodeId = newId;
        delete updatedEditingNode.data.options[idx].action;
        this.setState(function(s) {
            var nodes = s.nodes.map(function(n) {
                return n.id === updatedEditingNode.id ? Object.assign({}, n, { data: updatedEditingNode.data }) : n;
            }).concat([transferNode]);
            var autoEdges = (updatedEditingNode.data.options || []).filter(function(o) { return o.targetNodeId; }).map(function(o) {
                return { id: 'e_' + updatedEditingNode.id + '_' + o.key, source: updatedEditingNode.id, target: o.targetNodeId, label: o.key || '' };
            });
            var otherEdges = s.edges.filter(function(e) { return e.source !== updatedEditingNode.id; });
            return { nodes: nodes, edges: otherEdges.concat(autoEdges), isDirty: true, editingNode: updatedEditingNode, selectedNodeId: updatedEditingNode.id };
        });
    }

    createCustomBlockChildForOption = (idx, blockId) => {
        var settings = ((this.props.data.IVRFlows || {}).settings) || {};
        var block = (settings.customBlocks || {})[blockId];
        if (!block) {
            this.setState({ error: 'Custom block "' + blockId + '" is not configured for this domain.' });
            return;
        }
        var newId = newNodeId();
        var customNode = {
            id: newId, type: 'customNode', position: { x: 0, y: 0 },
            data: {
                blockId: blockId,
                label: block.name || blockId,
                options: (block.exits || []).map(function(ex) {
                    return { key: ex.key, label: ex.label, targetNodeId: '' };
                })
            }
        };
        var updatedEditingNode = JSON.parse(JSON.stringify(this.state.editingNode));
        updatedEditingNode.data.options[idx].targetNodeId = newId;
        delete updatedEditingNode.data.options[idx].action;
        this.setState(function(s) {
            var nodes = s.nodes.map(function(n) {
                return n.id === updatedEditingNode.id ? Object.assign({}, n, { data: updatedEditingNode.data }) : n;
            }).concat([customNode]);
            var autoEdges = (updatedEditingNode.data.options || []).filter(function(o) { return o.targetNodeId; }).map(function(o) {
                return { id: 'e_' + updatedEditingNode.id + '_' + o.key, source: updatedEditingNode.id, target: o.targetNodeId, label: o.key || '' };
            });
            var otherEdges = s.edges.filter(function(e) { return e.source !== updatedEditingNode.id; });
            return { nodes: nodes, edges: otherEdges.concat(autoEdges), isDirty: true, editingNode: updatedEditingNode, selectedNodeId: updatedEditingNode.id };
        });
    }

    getDescendants = (rootId, nodes, edges) => {
        var childMap = {};
        nodes.forEach(function(n) { childMap[n.id] = []; });
        edges.forEach(function(e) { if (childMap[e.source]) childMap[e.source].push(e.target); });
        var result = [];
        var queue = [rootId];
        var seen = {};
        seen[rootId] = true;
        while (queue.length) {
            var cur = queue.shift();
            (childMap[cur] || []).forEach(function(kid) {
                if (!seen[kid]) { seen[kid] = true; result.push(kid); queue.push(kid); }
            });
        }
        return result;
    }

    toggleCollapseNode = (nodeId, e) => {
        e.stopPropagation();
        this.setState(function(s) {
            var cn = Object.assign({}, s.collapsedNodes);
            if (cn[nodeId]) delete cn[nodeId]; else cn[nodeId] = true;
            return { collapsedNodes: cn };
        });
    }

    measureNode = (nodeId, height) => {
        // Only setState if the height actually changed by >0.5px to avoid render loops.
        var current = this.state.nodeHeights[nodeId];
        if (current && Math.abs(current - height) < 0.5) return;
        this.setState(function(s) {
            var next = Object.assign({}, s.nodeHeights);
            next[nodeId] = height;
            return { nodeHeights: next };
        });
    }

    requestDeleteNode = () => {
        var descendants = this.getDescendants(this.state.editingNode.id, this.state.nodes, this.state.edges);
        if (descendants.length > 0) {
            this.setState({ confirmingNodeDelete: true });
        } else {
            this.doDeleteNode();
        }
    }

    doDeleteNode = () => {
        var id = this.state.editingNode.id;
        this.setState(function(s) {
            var toDelete = [id].concat(this.getDescendants(id, s.nodes, s.edges));
            var toDeleteSet = {};
            toDelete.forEach(function(d) { toDeleteSet[d] = true; });
            var nodes = s.nodes.filter(function(n) { return !toDeleteSet[n.id]; });
            var edges = s.edges.filter(function(e) { return !toDeleteSet[e.source] && !toDeleteSet[e.target]; });
            // Clear targetNodeId refs in surviving nodes
            nodes = nodes.map(function(n) {
                var data = JSON.parse(JSON.stringify(n.data));
                if (data.options) {
                    data.options = data.options.map(function(opt) {
                        if (toDeleteSet[opt.targetNodeId]) { opt.targetNodeId = ''; }
                        return opt;
                    });
                }
                return Object.assign({}, n, { data: data });
            });
            return { nodes: nodes, edges: edges, editingNode: null, selectedNodeId: null, isDirty: true, confirmingNodeDelete: false };
        }.bind(this));
    }

    // ── Create child from node editor ─────────────────────────────────────────
    createChildNodeForOption = (idx) => {
        var newId = newNodeId();
        var newNode = {
            id: newId, type: 'ivrNode', position: { x: 0, y: 0 },
            data: { label: 'New Node', ttsTextByLang: {}, options: [], noInput: { action: 'repeat' }, noMatch: { action: 'repeat' } }
        };
        // Update the editing node's option to point to the new node
        var updatedEditingNode = JSON.parse(JSON.stringify(this.state.editingNode));
        updatedEditingNode.data.options[idx].targetNodeId = newId;
        delete updatedEditingNode.data.options[idx].action;

        // Save the parent node and add the child, then open child editor
        this.setState(function(s) {
            var nodes = s.nodes.map(function(n) {
                return n.id === updatedEditingNode.id ? Object.assign({}, n, { data: updatedEditingNode.data }) : n;
            }).concat([newNode]);
            // The new option's edge gets rebuilt below as part of autoEdges
            // (the option at idx was just pointed at newId), so no separate
            // newEdge is needed here.
            var otherEdges = s.edges.filter(function(e) { return e.source !== updatedEditingNode.id; });
            var autoEdges = (updatedEditingNode.data.options || []).filter(function(o) { return o.targetNodeId; }).map(function(o) {
                return { id: 'e_' + updatedEditingNode.id + '_' + o.key, source: updatedEditingNode.id, target: o.targetNodeId, label: o.key || '' };
            });
            return { nodes: nodes, edges: otherEdges.concat(autoEdges), isDirty: true, editingNode: JSON.parse(JSON.stringify(updatedEditingNode)), selectedNodeId: updatedEditingNode.id };
        });
    }

    // ── Add child node via "+" on option ──────────────────────────────────────
    addChildNode = (parentNode, optionIdx) => {
        var newId = newNodeId();
        var newNode = {
            id: newId,
            type: 'ivrNode',
            position: { x: 0, y: 0 },
            data: {
                label: 'New Node',
                ttsTextByLang: {},
                options: [],
                noInput: { action: 'repeat' },
                noMatch: { action: 'repeat' }
            }
        };
        this.setState(function(s) {
            var nodes = s.nodes.concat([newNode]);
            var updatedNodes = nodes.map(function(n) {
                if (n.id !== parentNode.id) return n;
                var data = JSON.parse(JSON.stringify(n.data));
                if (data.options && data.options[optionIdx] !== undefined) {
                    data.options[optionIdx].targetNodeId = newId;
                    delete data.options[optionIdx].action;
                }
                return Object.assign({}, n, { data: data });
            });
            var opt = (parentNode.data.options || [])[optionIdx] || {};
            var newEdge = { id: 'e_' + parentNode.id + '_' + (opt.key || optionIdx), source: parentNode.id, target: newId, label: opt.key || '' };
            var edges = s.edges.concat([newEdge]);
            return { nodes: updatedNodes, edges: edges, isDirty: true, editingNode: JSON.parse(JSON.stringify(newNode)), selectedNodeId: newId };
        });
    }

    // ── Save ───────────────────────────────────────────────────────────────────
    saveFlow = () => {
        var flows = ((this.props.data.IVRFlows || {}).data) || {};
        var current = flows[this.state.activeFlowId] || {};
        var flow = Object.assign({}, current, { id: this.state.activeFlowId, nodes: this.state.nodes, edges: this.state.edges, languages: this.state.languages });
        this.props.ws.send(JSON.stringify({ route: 'saveIVRFlow', flow: flow }));
        this.setState({ loading: true, error: '' });
    }

    // ── Flow management ────────────────────────────────────────────────────────
    openAddFlow = () => { this.setState({ addingFlow: true, newFlowName: '', error: '' }); }

    confirmAddFlow = () => {
        var name = this.state.newFlowName.trim();
        if (!name) { this.setState({ error: 'Please enter a name.' }); return; }
        var flowId = 'flow_' + name.toLowerCase().replace(/[^a-z0-9]/g, '') + '_' + Date.now().toString(36);
        var entryId = newNodeId();
        var menuId = newNodeId();
        var langs = [{ code: DEFAULT_LANG, label: 'English (US)', isDefault: true }];
        var flow = {
            id: flowId, name: name,
            languages: langs,
            nodes: [
                { id: entryId, type: 'entryNode', position: { x: 0, y: 0 }, data: { label: 'Entry', ttsTextByLang: { 'en-US': 'Thank you for calling.' }, next: { targetNodeId: menuId } } },
                { id: menuId, type: 'ivrNode', position: { x: 0, y: 0 }, data: { label: 'Main Menu', ttsTextByLang: { 'en-US': 'Please listen to the following options.' }, options: [], noInput: { action: 'repeat' }, noMatch: { action: 'repeat' } } }
            ],
            edges: []
        };
        this.props.ws.send(JSON.stringify({ route: 'saveIVRFlow', flow: flow }));
        this.setState({ addingFlow: false, loading: true, activeFlowId: flowId, nodes: flow.nodes, edges: [], languages: langs, activeLang: DEFAULT_LANG, isDirty: false, newFlowName: '', error: '' });
    }

    openRenameFlow = () => {
        var flows = ((this.props.data.IVRFlows || {}).data) || {};
        var current = flows[this.state.activeFlowId] || {};
        this.setState({ renamingFlow: true, flowMenuOpen: false, newFlowName: current.name || '', error: '' });
    }

    confirmRenameFlow = () => {
        var name = this.state.newFlowName.trim();
        if (!name) { this.setState({ error: 'Please enter a name.' }); return; }
        var flows = ((this.props.data.IVRFlows || {}).data) || {};
        var current = flows[this.state.activeFlowId] || {};
        var flow = Object.assign({}, current, { name: name, nodes: this.state.nodes, edges: this.state.edges });
        this.props.ws.send(JSON.stringify({ route: 'saveIVRFlow', flow: flow }));
        this.setState({ renamingFlow: false, loading: true, newFlowName: '', error: '' });
    }

    openDeleteFlow = () => { this.setState({ deletingFlow: true, flowMenuOpen: false }); }

    confirmDeleteFlow = () => {
        this.props.ws.send(JSON.stringify({ route: 'deleteIVRFlow', flowId: this.state.activeFlowId }));
        this.setState({ deletingFlow: false, loading: true, activeFlowId: '', nodes: [], edges: [], isDirty: false });
    }

    duplicateFlow = () => {
        var flows = ((this.props.data.IVRFlows || {}).data) || {};
        var source = flows[this.state.activeFlowId];
        if (!source) return;
        var newId2 = 'flow_' + Date.now().toString(36);
        var flow = JSON.parse(JSON.stringify(source));
        flow.id = newId2;
        flow.name = (source.name || 'Flow') + ' Copy';
        this.props.ws.send(JSON.stringify({ route: 'saveIVRFlow', flow: flow }));
        this.setState({ flowMenuOpen: false, loading: true, activeFlowId: newId2, nodes: flow.nodes, edges: flow.edges, isDirty: false });
    }

    // ── Canvas zoom (scroll wheel) ─────────────────────────────────────────────
    onCanvasWheel = (e) => {
        e.preventDefault();
        var factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
        var rect = this._containerRef.getBoundingClientRect();
        // Mouse position relative to container
        var mx = e.clientX - rect.left;
        var my = e.clientY - rect.top;
        this.setState(function(s) {
            var newZoom = Math.min(Math.max(s.zoom * factor, 0.15), 3);
            // Adjust pan so the point under the cursor stays fixed
            var newPanX = mx - (mx - s.panX) * (newZoom / s.zoom);
            var newPanY = my - (my - s.panY) * (newZoom / s.zoom);
            return { zoom: newZoom, panX: newPanX, panY: newPanY };
        });
    }

    // ── Canvas pan ─────────────────────────────────────────────────────────────
    onCanvasMouseDown = (e) => {
        if (e.button !== 0) return;
        this._drag = { startX: e.clientX, startY: e.clientY, panX: this.state.panX, panY: this.state.panY };
        this._dragMoved = false;
        this._pendingPan = null;
        if (this._containerRef) { this._containerRef.style.cursor = 'grabbing'; }
        e.preventDefault();
    }
    onCanvasMouseMove = (e) => {
        if (!this._drag) return;
        var dx = e.clientX - this._drag.startX;
        var dy = e.clientY - this._drag.startY;
        if (Math.abs(dx) > 4 || Math.abs(dy) > 4) this._dragMoved = true;
        var nx = this._drag.panX + dx;
        var ny = this._drag.panY + dy;
        this._pendingPan = { x: nx, y: ny };
        if (this._canvasRef) {
            this._canvasRef.style.transform = 'translate(' + nx + 'px,' + ny + 'px) scale(' + this.state.zoom + ')';
        }
    }
    onCanvasMouseUp = () => {
        if (!this._drag) return;
        if (this._containerRef) { this._containerRef.style.cursor = 'grab'; }
        var wasDrag = this._dragMoved;
        this._drag = null;
        if (!wasDrag) {
            this.setState({ selectedNodeId: null });
        } else if (this._pendingPan) {
            this.setState({ panX: this._pendingPan.x, panY: this._pendingPan.y });
        }
    }

    cancel = () => {
        this.setState({ addingFlow: false, renamingFlow: false, deletingFlow: false, managingLanguages: false, flowMenuOpen: false, editingNode: null, loading: false, error: '', confirmingNodeDelete: false, editingAlternativesIdx: null, optionTypeHints: {}, keypadOpenIdx: null });
    }

    setOptionKey = (optIdx, newKey) => {
        this.setState(function(s) {
            var node = JSON.parse(JSON.stringify(s.editingNode));
            if (!node.data.options || !node.data.options[optIdx]) return {};
            node.data.options[optIdx].key = newKey;
            // Sort options by phone-keypad order (1-9, *, 0, #)
            node.data.options.sort(function(a, b) { return dtmfSortValue(a.key) - dtmfSortValue(b.key); });
            // Sort breaks the optIdx → option mapping, so reset transient per-index state.
            return { editingNode: node, keypadOpenIdx: null, optionTypeHints: {}, editingAlternativesIdx: null };
        });
    }

    toggleKeypad = (optIdx) => {
        this.setState(function(s) {
            return { keypadOpenIdx: s.keypadOpenIdx === optIdx ? null : optIdx };
        });
    }

    // ── Language management ────────────────────────────────────────────────────
    openManageLanguages = () => { this.setState({ managingLanguages: true, flowMenuOpen: false, error: '' }); }

    setActiveLang = (code) => { this.setState({ activeLang: code }); }

    addLanguageToFlow = (code, label) => {
        if (!code) return;
        var existing = (this.state.languages || []).find(function(l) { return l.code === code; });
        if (existing) { this.setState({ activeLang: code }); return; }
        var newLang = { code: code, label: label || code, isDefault: false };
        var newLangs = (this.state.languages || []).concat([newLang]);
        var nodes = this.state.nodes;

        // Two paths:
        // 1) Flow already has a separate languagePickerNode (legacy multilingual flow):
        //    keep using it — append a new option to the picker.
        // 2) Otherwise: integrate language selection directly into the entry node by
        //    converting `entry.data.next` into `entry.data.languageOptions[]`.
        var hasSeparatePicker = nodes.some(function(n) { return n.type === 'languagePickerNode'; });
        if (hasSeparatePicker) {
            nodes = nodes.map(function(n) {
                if (n.type !== 'languagePickerNode') return n;
                var data = JSON.parse(JSON.stringify(n.data));
                data.options = data.options || [];
                var usedKeys = {}; data.options.forEach(function(o) { if (o.key) usedKeys[o.key] = true; });
                var nextKey = KEYPAD_KEYS.find(function(k) { return !usedKeys[k]; }) || String(data.options.length + 1);
                data.options.push({ key: nextKey, label: label || code, setsLanguage: code, targetNodeId: '', voiceAlternatives: [] });
                return Object.assign({}, n, { data: data });
            });
        } else {
            nodes = this.addLanguageToEntry(nodes, newLangs, newLang);
        }
        this.setState({ languages: newLangs, nodes: nodes, activeLang: code, isDirty: true });
    }

    // Mutates the entry node so language selection is part of it. The entry's
    // `languageOptions` is just the DTMF key ↔ language mapping for runtime — once
    // the caller picks a language, all paths converge at the entry's shared
    // `data.next` target. So `addLanguageToEntry` adds the new language's
    // {key, setsLanguage, label} entry and leaves `data.next` alone.
    addLanguageToEntry = (nodes, allLangs, newLang) => {
        return nodes.map(function(n) {
            if (n.type !== 'entryNode') return n;
            var data = JSON.parse(JSON.stringify(n.data || {}));
            if (Array.isArray(data.languageOptions) && data.languageOptions.length) {
                var usedKeys = {};
                data.languageOptions.forEach(function(o) { if (o.key) usedKeys[o.key] = true; });
                var nextKey = KEYPAD_KEYS.find(function(k) { return !usedKeys[k]; }) || String(data.languageOptions.length + 1);
                data.languageOptions.push({ key: nextKey, setsLanguage: newLang.code, label: newLang.label, voiceAlternatives: [] });
            } else {
                // 1→2 transition. Initialize languageOptions with one entry per existing
                // language. data.next stays as it was — it's now the shared target.
                data.languageOptions = allLangs.map(function(l, i) {
                    return { key: String(i + 1), setsLanguage: l.code, label: l.label, voiceAlternatives: [] };
                });
            }
            return Object.assign({}, n, { data: data });
        });
    }

    // Builds a Language Picker node and wires it into the flow. If there's an entry
    // node, the picker slots between entry.next and its current target — entry → picker
    // → (previous target). Otherwise the picker becomes the new root (existing root
    // becomes the default language's destination).
    injectLanguagePicker = (nodes, langs) => {
        var defaultLang = langs.find(function(l) { return l.isDefault; }) || langs[0];
        var pickerId = newNodeId();

        var entry = nodes.find(function(n) { return n.type === 'entryNode'; });
        var defaultTargetId = '';
        var updatedNodes = nodes;

        if (entry) {
            // Picker slips in between the entry and its previous target
            var prevTarget = (entry.data && entry.data.next && entry.data.next.targetNodeId) || '';
            defaultTargetId = prevTarget;
            updatedNodes = nodes.map(function(n) {
                if (n.id !== entry.id) return n;
                var data = JSON.parse(JSON.stringify(n.data || {}));
                data.next = { targetNodeId: pickerId };
                return Object.assign({}, n, { data: data });
            });
        } else {
            // No entry node: picker becomes the new root, existing root becomes default lang's destination
            var hasIncoming = {};
            nodes.forEach(function(n) {
                ((n.data && n.data.options) || []).forEach(function(opt) {
                    if (opt.targetNodeId) hasIncoming[opt.targetNodeId] = true;
                });
            });
            var existingRoot = nodes.find(function(n) { return !hasIncoming[n.id]; }) || nodes[0];
            defaultTargetId = existingRoot ? existingRoot.id : '';
        }

        var pickerOptions = langs.map(function(l, i) {
            return {
                key: String(i + 1),
                label: l.label,
                setsLanguage: l.code,
                targetNodeId: l.code === defaultLang.code ? defaultTargetId : '',
                voiceAlternatives: []
            };
        });
        var pickerNode = {
            id: pickerId,
            type: 'languagePickerNode',
            position: { x: 0, y: 0 },
            data: {
                label: 'Language Selection',
                ttsText: 'For English, press 1. Para español, oprima 2.',
                options: pickerOptions,
                noInput: { action: 'repeat' },
                noMatch: { action: 'repeat' }
            }
        };
        return entry ? updatedNodes.concat([pickerNode]) : [pickerNode].concat(updatedNodes);
    }

    removeLanguageFromFlow = (code) => {
        var langs = this.state.languages || [];
        if (langs.length <= 1) return;
        var filtered = langs.filter(function(l) { return l.code !== code; });
        // If we removed the default, promote the first remaining language
        if (!filtered.some(function(l) { return l.isDefault; })) {
            filtered = filtered.map(function(l, i) { return Object.assign({}, l, { isDefault: i === 0 }); });
        }
        // Strip the language's content from every node, remove its picker option,
        // and prune entry.languageOptions (collapsing back to single `next` if only
        // one language remains).
        var nodes = this.state.nodes.map(function(n) {
            var data = JSON.parse(JSON.stringify(n.data || {}));
            if (data.ttsTextByLang) delete data.ttsTextByLang[code];
            if (data.promptByLang) delete data.promptByLang[code];
            if (data.options) {
                data.options = data.options
                    .filter(function(opt) { return opt.setsLanguage !== code; })
                    .map(function(opt) {
                        if (opt.voiceAlternativesByLang) delete opt.voiceAlternativesByLang[code];
                        return opt;
                    });
            }
            if (n.type === 'entryNode' && Array.isArray(data.languageOptions)) {
                data.languageOptions = data.languageOptions.filter(function(o) { return o.setsLanguage !== code; });
                // Once we're back to a single language, the keypad mapping is moot —
                // strip languageOptions and let data.next continue to be the target.
                if (data.languageOptions.length <= 1) {
                    delete data.languageOptions;
                }
            }
            return Object.assign({}, n, { data: data });
        });
        this.setState({ languages: filtered, nodes: nodes, activeLang: this.state.activeLang === code ? filtered[0].code : this.state.activeLang, isDirty: true });
    }

    setDefaultLanguage = (code) => {
        var langs = (this.state.languages || []).map(function(l) { return Object.assign({}, l, { isDefault: l.code === code }); });
        this.setState({ languages: langs, isDirty: true });
    }

    renameLanguage = (code, newLabel) => {
        var langs = (this.state.languages || []).map(function(l) { return l.code === code ? Object.assign({}, l, { label: newLabel }) : l; });
        // Mirror the new label onto any node that selects this code: separate picker
        // nodes (legacy) and entry-block language options (new pattern).
        var nodes = this.state.nodes.map(function(n) {
            var data = JSON.parse(JSON.stringify(n.data || {}));
            if (n.type === 'languagePickerNode') {
                (data.options || []).forEach(function(opt) {
                    if (opt.setsLanguage === code) opt.label = newLabel;
                });
            }
            if (n.type === 'entryNode' && Array.isArray(data.languageOptions)) {
                data.languageOptions.forEach(function(opt) {
                    if (opt.setsLanguage === code) opt.label = newLabel;
                });
            }
            return Object.assign({}, n, { data: data });
        });
        this.setState({ languages: langs, nodes: nodes, isDirty: true });
    }

    setOptionType = (optIdx, newType) => {
        this.setState(function(s) {
            var hints = Object.assign({}, s.optionTypeHints);
            hints[optIdx] = newType;
            var node = JSON.parse(JSON.stringify(s.editingNode));
            var opt = node.data.options[optIdx];
            if (opt) {
                delete opt.action;
                opt.targetNodeId = '';
                delete opt.targetFlowId;
            }
            return { optionTypeHints: hints, editingNode: node };
        });
    }

    render() {
        if (!this.props.active) return <div></div>;

        var flows = ((this.props.data.IVRFlows || {}).data) || {};
        var settings = ((this.props.data.IVRFlows || {}).settings) || {};
        var customBlocks = settings.customBlocks || {};
        var flowKeys = Object.keys(flows);
        var activeFlow = flows[this.state.activeFlowId] || {};
        var nodes = this.state.nodes;

        // Derive edges directly from node options to ensure they're always in sync
        var derivedEdges = [];
        nodes.forEach(function(n) {
            // Entry nodes always have one outgoing edge: data.next. languageOptions
            // (if present) is just the DTMF↔language mapping — all paths converge
            // on data.next after the caller picks a language.
            if (n.type === 'entryNode') {
                var entryNext = (n.data || {}).next || {};
                if (entryNext.targetNodeId) {
                    derivedEdges.push({ id: 'e_' + n.id + '_next', source: n.id, target: entryNext.targetNodeId });
                }
                return;
            }
            (n.data.options || []).forEach(function(opt) {
                if (opt.targetNodeId) {
                    derivedEdges.push({ id: 'e_' + n.id + '_' + (opt.key || opt.targetNodeId), source: n.id, target: opt.targetNodeId });
                }
            });
        });

        // Build a cycle-safe childMap for collapse/count logic (same approach as buildLayout)
        var allChildMap = {};
        var hasParentC = {};
        nodes.forEach(function(n) { allChildMap[n.id] = []; });
        function canReachC(from, to) {
            var queue = [from], seen = {}; seen[from] = true;
            while (queue.length) {
                var cur = queue.shift();
                if (cur === to) return true;
                (allChildMap[cur] || []).forEach(function(k) { if (!seen[k]) { seen[k] = true; queue.push(k); } });
            }
            return false;
        }
        derivedEdges.forEach(function(e) {
            if (!allChildMap[e.source] || allChildMap[e.target] === undefined) return;
            if (hasParentC[e.target]) return;
            if (canReachC(e.target, e.source)) return;
            allChildMap[e.source].push(e.target);
            hasParentC[e.target] = true;
        });

        // Count unique descendants per node via BFS. Inner traversal uses a
        // plain for-loop instead of forEach so we don't declare a function
        // inside the while (avoids the no-loop-func lint warning).
        var descendantCounts = {};
        nodes.forEach(function(n) {
            var visited = {}; var count = 0;
            var queue = [n.id]; visited[n.id] = true;
            while (queue.length) {
                var cur = queue.shift();
                var kids = allChildMap[cur] || [];
                for (var i = 0; i < kids.length; i++) {
                    var kid = kids[i];
                    if (!visited[kid]) { visited[kid] = true; count++; queue.push(kid); }
                }
            }
            descendantCounts[n.id] = count;
        });

        // Compute hidden nodes (descendants of collapsed nodes) via BFS
        var collapsedNodes = this.state.collapsedNodes;
        var hiddenNodeIds = {};
        nodes.forEach(function(n) {
            if (!collapsedNodes[n.id]) return;
            var queue = allChildMap[n.id].slice(); var vis = {};
            while (queue.length) {
                var cur = queue.shift();
                if (vis[cur]) continue; vis[cur] = true;
                hiddenNodeIds[cur] = true;
                (allChildMap[cur] || []).forEach(function(kid) { if (!vis[kid]) queue.push(kid); });
            }
        });
        var visibleNodes = nodes.filter(function(n) { return !hiddenNodeIds[n.id]; });
        var edges = derivedEdges.filter(function(e) { return !hiddenNodeIds[e.source] && !hiddenNodeIds[e.target]; });

        // Compute layout using only visible nodes/edges
        var layout = buildLayout(visibleNodes, edges, this.state.nodeHeights);
        var positions = layout.positions;
        var colorMap = computeColorMap(visibleNodes, edges);
        // Build map of nodeId -> DTMF key (from parent options)
        var keyMap = {};
        nodes.forEach(function(n) {
            (n.data.options || []).forEach(function(opt) {
                if (opt.targetNodeId && opt.key && !keyMap[opt.targetNodeId]) {
                    keyMap[opt.targetNodeId] = opt.key;
                }
            });
        });
        var canvasW = Math.max(layout.width + 60, 600);
        var canvasH = Math.max(layout.height + 60, 400);
        var queuesRaw = ((this.props.data.Queues || {}).data) || {};
        var queuesList = Object.keys(queuesRaw).map(function(id) { return queuesRaw[id]; });

        // ── Node editor modal ──────────────────────────────────────────────────
        var nodeModal = null;
        if (this.state.editingNode && this.state.editingNode.type === 'transferNode') {
            var tn = this.state.editingNode;
            var tnData = tn.data || {};
            nodeModal = (
                <div className="blackout" tabIndex="1" autoFocus>
                    <div className="pop" style={{ display: 'flex', flexDirection: 'column', width: '420px', maxWidth: '94vw', padding: '0', position: 'relative', overflow: 'hidden' }}>
                        <div className="viewCloser" onClick={this.cancel}></div>
                        <div style={{ background: '#002f5f', padding: '22px 28px 16px', color: '#fff' }}>
                            <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: '5px' }}>Transfer Node</div>
                            <div style={{ fontSize: '21px', fontWeight: '700' }}>↗ Transfer Destination</div>
                        </div>
                        <div style={{ padding: '22px 28px', overflowY: 'auto', maxHeight: 'calc(80vh - 90px)' }}>
                            <label style={labelStyle}>Destination Type</label>
                            <select value={tnData.transferType || 'phoneNumber'} onChange={this.editTransferNodeField('transferType')} style={inputStyle}>
                                <option value="phoneNumber">Phone Number</option>
                                <option value="queue">Queue</option>
                            </select>
                            {(!tnData.transferType || tnData.transferType === 'phoneNumber') ? (
                                <div>
                                    <label style={labelStyle}>Phone Number</label>
                                    <input type="text" value={tnData.transferPhone || ''} onChange={this.editTransferNodeField('transferPhone')}
                                        placeholder="e.g. +1 (555) 867-5309" style={inputStyle} />
                                </div>
                            ) : (
                                <div>
                                    <label style={labelStyle}>Queue</label>
                                    <select value={tnData.transferQueueId || ''} onChange={this.editTransferNodeField('transferQueueId')} style={inputStyle}>
                                        <option value="">— Select a Queue —</option>
                                        {queuesList.map(function(q) {
                                            return <option key={q.id} value={q.id}>{q.name}</option>;
                                        })}
                                    </select>
                                </div>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '8px' }}>
                                <DeleteButton onClick={this.requestDeleteNode} />
                                <SaveButton onClick={this.saveNode}>Save</SaveButton>
                            </div>
                        </div>
                    </div>
                </div>
            );
        } else if (this.state.editingNode && this.state.editingNode.type === 'customNode') {
            var cn = this.state.editingNode;
            var cnData = cn.data || {};
            var cnBlock = customBlocks[cnData.blockId];
            var cnBlockMissing = !cnBlock;
            var cnHeaderColor = '#002f5f';
            var cnOtherNodes = nodes.filter(function(n) { return n.id !== cn.id; });
            var cnAncestorIds = computeTreeAncestors(cn.id, nodes, edges);
            var cnAncestorNodes = cnOtherNodes.filter(function(n) { return cnAncestorIds[n.id] && n.type !== 'customNode' && n.type !== 'transferNode'; });
            nodeModal = (
                <div className="blackout" tabIndex="1" autoFocus>
                    <div className="pop" style={{ display: 'flex', flexDirection: 'column', width: '560px', maxWidth: '94vw', padding: '0', position: 'relative', overflow: 'hidden' }}>
                        <div className="viewCloser" onClick={this.cancel}></div>
                        <div style={{ background: cnHeaderColor, padding: '22px 28px 16px', color: '#fff' }}>
                            <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)', marginBottom: '5px' }}>Custom Block</div>
                            <div style={{ fontSize: '21px', fontWeight: '700' }}>{(cnBlock && cnBlock.name) || cnData.label || cnData.blockId || 'Custom Block'}</div>
                            {cnBlock && cnBlock.description && (
                                <div style={{ marginTop: '6px', fontSize: '12px', color: 'rgba(255,255,255,0.85)', lineHeight: '1.4' }}>{cnBlock.description}</div>
                            )}
                        </div>
                        <div style={{ padding: '22px 28px', overflowY: 'auto', maxHeight: 'calc(80vh - 90px)' }}>
                            {cnBlockMissing && (
                                <div style={{ background: '#fff5f5', border: '1px solid #f5c6cb', color: '#c0392b', borderRadius: '6px', padding: '10px 12px', fontSize: '12px', marginBottom: '16px' }}>
                                    Block definition <strong>{cnData.blockId}</strong> isn't configured for this domain. The block will keep its saved wiring but cannot be edited until it's added to <code>IVRFlows.settings.customBlocks</code>.
                                </div>
                            )}
                            <label style={labelStyle}>Display Label</label>
                            <input type="text" value={cnData.label || ''} onChange={this.editNodeField('label')} style={inputStyle} placeholder={(cnBlock && cnBlock.name) || cnData.blockId} />

                            {(cnData.options || []).length > 0 && (
                                <div>
                                    <label style={labelStyle}>Exits</label>
                                    {(cnData.options || []).map(function(opt, idx) {
                                        var targetNode = opt.targetNodeId ? nodes.find(function(n) { return n.id === opt.targetNodeId; }) : null;
                                        var derivedType = '';
                                        if (opt.targetFlowId) {
                                            derivedType = 'flow';
                                        } else if (opt.action) {
                                            derivedType = 'action';
                                        } else if (targetNode) {
                                            if (targetNode.type === 'transferNode') derivedType = 'action';
                                            else if (targetNode.type === 'customNode') derivedType = 'block';
                                            else derivedType = 'menu';
                                        }
                                        var hint = this.state.optionTypeHints[idx];
                                        var displayedType = (hint != null && hint !== '') ? hint : derivedType;
                                        var actionVal = opt.action ? ('action:' + opt.action) :
                                            (targetNode && targetNode.type === 'transferNode' ? 'action:transfer' : '');
                                        var menuVal = (targetNode && targetNode.type === 'ivrNode') ? targetNode.id : '';
                                        var blockVal = (targetNode && targetNode.type === 'customNode') ? targetNode.id : '';
                                        var flowVal = opt.targetFlowId ? ('flow:' + opt.targetFlowId) : '';
                                        var linkedMenuChild = (menuVal && !cnAncestorIds[menuVal]) ? targetNode : null;
                                        var activeFlowId = this.state.activeFlowId;
                                        var otherFlows = Object.keys(flows).filter(function(fid) { return fid !== activeFlowId; }).map(function(fid) { return flows[fid]; });
                                        var subStyle = { flex: 1, padding: '8px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '13px', background: '#fff' };
                                        return (
                                            <div key={idx} style={{ border: '1px solid #e3e7ee', borderRadius: '8px', padding: '10px 12px', marginBottom: '8px', background: '#fafbfd' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                                    <span style={{ background: BLOCK_CHIP, color: '#fff', borderRadius: '4px', padding: '2px 8px', fontSize: '11px', fontWeight: '700', flexShrink: 0 }}>{opt.key || '?'}</span>
                                                    <span style={{ fontSize: '13px', color: '#222', fontWeight: '600' }}>{opt.label || '—'}</span>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <select
                                                        value={displayedType}
                                                        onChange={function(idx2, e) { this.setOptionType(idx2, e.target.value); }.bind(this, idx)}
                                                        style={{ width: '140px', padding: '8px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '13px', background: '#fff', flexShrink: 0 }}
                                                    >
                                                        <option value="">— Type —</option>
                                                        <option value="menu">Menu Node</option>
                                                        <option value="block">Block</option>
                                                        <option value="action">Action</option>
                                                        {otherFlows.length > 0 && <option value="flow">Flow</option>}
                                                    </select>
                                                    {displayedType === 'flow' && (
                                                        <select value={flowVal} onChange={function(idx2, e) { this.editNodeOptionTarget(idx2, e.target.value); }.bind(this, idx)} style={subStyle}>
                                                            <option value="">— Pick flow —</option>
                                                            {otherFlows.map(function(f) {
                                                                return <option key={f.id} value={'flow:' + f.id}>↪ {f.name || f.id}</option>;
                                                            })}
                                                        </select>
                                                    )}
                                                    {displayedType === 'menu' && (
                                                        <select value={menuVal} onChange={function(idx2, e) { this.editNodeOptionTarget(idx2, e.target.value); }.bind(this, idx)} style={subStyle}>
                                                            <option value="">— Pick menu —</option>
                                                            <option value="newMenu:default">+ New Menu Node</option>
                                                            {linkedMenuChild && <option value={linkedMenuChild.id}>→ {(linkedMenuChild.data || {}).label || linkedMenuChild.id}</option>}
                                                            {cnAncestorNodes.length > 0 && <option disabled>── Return to Parent ──</option>}
                                                            {cnAncestorNodes.map(function(n) {
                                                                return <option key={'anc_' + n.id} value={n.id}>↑ {(n.data || {}).label || n.id}</option>;
                                                            })}
                                                        </select>
                                                    )}
                                                    {displayedType === 'block' && (
                                                        <select value={blockVal} onChange={function(idx2, e) { this.editNodeOptionTarget(idx2, e.target.value); }.bind(this, idx)} style={subStyle}>
                                                            <option value="">— Pick block —</option>
                                                            {blockVal && targetNode && <option value={blockVal}>→ {(targetNode.data || {}).label || blockVal}</option>}
                                                            {Object.values(customBlocks).map(function(b) {
                                                                return <option key={b.id} value={'newBlock:' + b.id}>+ {b.name}</option>;
                                                            })}
                                                        </select>
                                                    )}
                                                    {displayedType === 'action' && (
                                                        <select value={actionVal} onChange={function(idx2, e) { this.editNodeOptionTarget(idx2, e.target.value); }.bind(this, idx)} style={subStyle}>
                                                            <option value="">— Pick action —</option>
                                                            <option value="action:repeat">Repeat</option>
                                                            <option value="action:hangup">Hang Up</option>
                                                            <option value="action:transfer">Transfer</option>
                                                        </select>
                                                    )}
                                                    {!displayedType && (
                                                        <div style={{ flex: 1, fontSize: '11px', color: '#aaa', fontStyle: 'italic', padding: '0 8px' }}>Pick a type to choose a destination</div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    }.bind(this))}
                                </div>
                            )}

                            {(cnData.options || []).length === 0 && (
                                <div style={{ fontSize: '12px', color: '#888', fontStyle: 'italic', marginBottom: '16px' }}>This block is terminal — it has no exits.</div>
                            )}

                            {this.state.confirmingNodeDelete && (
                                <div style={{ background: '#fff5f5', border: '1px solid #f5c6cb', borderRadius: '6px', padding: '12px 14px', marginBottom: '16px' }}>
                                    <div style={{ fontWeight: '700', color: '#c0392b', marginBottom: '6px', fontSize: '13px' }}>Delete all child nodes too?</div>
                                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>This block has children that will also be permanently deleted.</div>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button onClick={this.doDeleteNode} style={{ background: '#c0392b', border: 'none', borderRadius: '6px', color: '#fff', padding: '7px 14px', fontSize: '13px', cursor: 'pointer', fontWeight: '700' }}>Yes, delete all</button>
                                        <button onClick={function() { this.setState({ confirmingNodeDelete: false }); }.bind(this)} style={{ background: 'none', border: '1px solid #ccc', borderRadius: '6px', color: '#555', padding: '7px 14px', fontSize: '13px', cursor: 'pointer' }}>Cancel</button>
                                    </div>
                                </div>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
                                {nodes.length > 1 && <DeleteButton onClick={this.requestDeleteNode} />}
                                <SaveButton onClick={this.saveNode}>Save</SaveButton>
                            </div>
                        </div>
                    </div>
                </div>
            );
        } else if (this.state.editingNode && this.state.editingNode.type === 'entryNode') {
            var en = this.state.editingNode;
            var enData = en.data || {};
            var enNext = enData.next || {};
            var enActiveLang = this.state.activeLang;
            var enActiveLangObj = (this.state.languages || []).find(function(l) { return l.code === enActiveLang; }) || { code: enActiveLang, label: enActiveLang };
            var enMultilingual = (this.state.languages || []).length > 1;
            var enOtherFlows = Object.keys(flows).filter(function(fid) { return fid !== this.state.activeFlowId; }.bind(this)).map(function(fid) { return flows[fid]; });
            var enCandidates = nodes.filter(function(n) { return n.id !== en.id && n.type !== 'entryNode'; });
            // Determine current next-target type — either an explicit hint set when the
            // user changed the type select, or derived from the live data on data.next.
            var enHint = en._nextTypeHint;
            var enDerivedType = '';
            var enTargetNode = enNext.targetNodeId ? nodes.find(function(n) { return n.id === enNext.targetNodeId; }) : null;
            if (enNext.targetFlowId) enDerivedType = 'flow';
            else if (enNext.action) enDerivedType = 'action';
            else if (enTargetNode) {
                // Transfer + Language Selection are surfaced as actions in the Type
                // picker even though they store as targetNodeId (matches transfer's
                // existing behavior for ivrNode options).
                if (enTargetNode.type === 'transferNode') enDerivedType = 'action';
                else if (enTargetNode.type === 'languagePickerNode') enDerivedType = 'action';
                else if (enTargetNode.type === 'customNode') enDerivedType = 'block';
                else enDerivedType = 'menu';
            }
            var enType = (enHint != null && enHint !== '') ? enHint : enDerivedType;
            var enActionVal = enNext.action ? ('action:' + enNext.action) :
                (enTargetNode && enTargetNode.type === 'transferNode' ? 'action:transfer' :
                (enTargetNode && enTargetNode.type === 'languagePickerNode' ? 'action:languageSelection' : ''));
            var enMenuVal = (enTargetNode && enTargetNode.type === 'ivrNode') ? enTargetNode.id : '';
            var enBlockVal = (enTargetNode && enTargetNode.type === 'customNode') ? enTargetNode.id : '';
            var enFlowVal = enNext.targetFlowId ? ('flow:' + enNext.targetFlowId) : '';
            var enSubStyle = { flex: 1, padding: '8px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '13px', background: '#fff' };
            nodeModal = (
                <div className="blackout" tabIndex="1" autoFocus>
                    <div className="pop" style={{ display: 'flex', flexDirection: 'column', width: '620px', maxWidth: '94vw', padding: '0', position: 'relative', overflow: 'hidden' }}>
                        <div className="viewCloser" onClick={this.cancel}></div>
                        <div style={{ background: ENTRY_COLOR, padding: '22px 28px 16px', color: '#fff' }}>
                            <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: '5px' }}>Entry Block</div>
                            <div style={{ fontSize: '21px', fontWeight: '700' }}>▶ {enData.label || 'Entry'}</div>
                            {enMultilingual && (
                                <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginRight: '4px' }}>Editing</span>
                                    {(this.state.languages || []).map(function(l) {
                                        var active = l.code === this.state.activeLang;
                                        var hasContent = nodeHasTranslation(enData, l.code, l.code === flowDefaultLang({ languages: this.state.languages }));
                                        return (
                                            <button
                                                key={l.code}
                                                type="button"
                                                onClick={function(c) { this.setActiveLang(c); }.bind(this, l.code)}
                                                style={{
                                                    padding: '5px 11px', fontSize: '12px', fontWeight: '600',
                                                    border: '1px solid ' + (active ? '#fff' : 'rgba(255,255,255,0.35)'),
                                                    background: active ? '#fff' : 'transparent',
                                                    color: active ? ENTRY_COLOR : 'rgba(255,255,255,0.92)',
                                                    borderRadius: '14px', cursor: 'pointer',
                                                    display: 'inline-flex', alignItems: 'center', gap: '5px'
                                                }}
                                            >
                                                {l.label}
                                                {!hasContent && (
                                                    <span title="Missing translation" style={{ background: '#e67e22', color: '#fff', borderRadius: '8px', padding: '0 4px', fontSize: '9px', fontWeight: '700' }}>!</span>
                                                )}
                                            </button>
                                        );
                                    }.bind(this))}
                                </div>
                            )}
                        </div>
                        <div style={{ padding: '22px 28px', overflowY: 'auto', maxHeight: 'calc(80vh - 110px)' }}>
                            <label style={labelStyle}>Label</label>
                            <input type="text" value={enData.label || ''} onChange={this.editNodeField('label')} style={inputStyle} />

                            <label style={labelStyle}>Audio</label>
                            {(function() {
                                var mode = enData.audioMode === 'prompt' ? 'prompt' : 'tts';
                                var segBtn = function(active) {
                                    return {
                                        flex: 1, padding: '8px 10px', fontSize: '13px', fontWeight: '600',
                                        border: '1px solid ' + (active ? '#2A6AA9' : '#ccc'),
                                        background: active ? '#2A6AA9' : '#fff',
                                        color: active ? '#fff' : '#555',
                                        cursor: 'pointer'
                                    };
                                };
                                var ttsBtn = segBtn(mode === 'tts');
                                ttsBtn.borderRadius = '6px 0 0 6px';
                                var promptBtn = segBtn(mode === 'prompt');
                                promptBtn.borderRadius = '0 6px 6px 0';
                                promptBtn.borderLeft = 'none';
                                var Prompts = this.props.data && this.props.data.Prompts;
                                return (
                                    <div style={{ marginBottom: '20px' }}>
                                        <div style={{ display: 'flex', marginBottom: '10px' }}>
                                            <button type="button" style={ttsBtn} onClick={function() { this.setNodeAudioMode('tts'); }.bind(this)}>Text-to-Speech</button>
                                            <button type="button" style={promptBtn} onClick={function() { this.setNodeAudioMode('prompt'); }.bind(this)}>Prompt</button>
                                        </div>
                                        {mode === 'tts' ? (
                                            <textarea
                                                value={readTts(enData, enActiveLang)}
                                                onChange={this.editNodeTts}
                                                rows={4}
                                                placeholder={enMultilingual ? ('Greeting for ' + enActiveLangObj.label) : 'Caller greeting'}
                                                style={{ width: '100%', padding: '10px 12px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '14px', color: '#222', background: '#fff', boxSizing: 'border-box', resize: 'vertical' }}
                                            />
                                        ) : (
                                            Prompts && Prompts.data ? (
                                                <div style={{ border: '1px solid #d3d3d3', borderRadius: '8px', backgroundColor: '#f0f0f0', maxWidth: '440px', margin: '0 auto', display: 'flex', justifyContent: 'center' }}>
                                                    <PromptPicker
                                                        prompts={JSON.parse(JSON.stringify(Prompts))}
                                                        currentPrompt={readPrompt(enData, enActiveLang)}
                                                        selectionChange={this.setNodePrompt}
                                                        ws={this.props.ws}
                                                    />
                                                </div>
                                            ) : (
                                                <div style={{ padding: '10px 12px', border: '1px dashed #ccc', borderRadius: '6px', fontSize: '13px', color: '#888' }}>
                                                    No prompts available. Add prompts from the Prompts page first.
                                                </div>
                                            )
                                        )}
                                    </div>
                                );
                            }.bind(this))()}

                            {Array.isArray(enData.languageOptions) && (
                                <div style={{ marginBottom: '18px' }}>
                                    <label style={labelStyle}>Language Keys</label>
                                    <div style={{ fontSize: '11px', color: '#888', marginBottom: '10px', lineHeight: '1.45' }}>
                                        Caller presses one of these keys to choose a language. After selection, the call continues to the shared destination below.
                                    </div>
                                    {enData.languageOptions.map(function(opt, idx) {
                                        var lang = (this.state.languages || []).find(function(l) { return l.code === opt.setsLanguage; }) || { code: opt.setsLanguage, label: opt.setsLanguage };
                                        var keypadOpen = this.state.keypadOpenIdx === idx;
                                        return (
                                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', border: '1px solid #e3e7ee', borderRadius: '8px', marginBottom: '6px', background: '#fafbfd' }}>
                                                <div style={{ position: 'relative', flexShrink: 0 }}>
                                                    <button
                                                        type="button"
                                                        onClick={function(i, e) { e.stopPropagation(); this.toggleKeypad(i); }.bind(this, idx)}
                                                        title="Pick key"
                                                        style={{ width: '40px', height: '30px', padding: 0, border: '1px solid ' + (keypadOpen ? '#2A6AA9' : '#ccc'), borderRadius: '5px', fontSize: '14px', fontWeight: '700', textAlign: 'center', background: keypadOpen ? '#eaf2fb' : '#fff', color: opt.key ? '#222' : '#aaa', cursor: 'pointer' }}
                                                    >{opt.key || '?'}</button>
                                                    {keypadOpen && (function() {
                                                        var usedKeys = {};
                                                        (enData.languageOptions || []).forEach(function(o, oi) { if (oi !== idx && o.key) usedKeys[o.key] = true; });
                                                        return (
                                                            <React.Fragment>
                                                                <div onMouseDown={function(e) { e.stopPropagation(); this.setState({ keypadOpenIdx: null }); }.bind(this)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 2199, background: 'transparent' }} />
                                                                <div onMouseDown={function(e) { e.stopPropagation(); }} style={{ position: 'absolute', top: '34px', left: 0, zIndex: 2200, padding: '8px', background: '#fff', border: '1px solid #ccc', borderRadius: '8px', boxShadow: '0 4px 14px rgba(0,0,0,0.18)' }}>
                                                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 34px)', gap: '6px' }}>
                                                                        {KEYPAD_KEYS.map(function(k) {
                                                                            var used = !!usedKeys[k];
                                                                            var current = k === opt.key;
                                                                            return (
                                                                                <button key={k} type="button" disabled={used}
                                                                                    onClick={used ? undefined : function(i, kk) { this.setEntryLangOptionKey(i, kk); }.bind(this, idx, k)}
                                                                                    style={{ width: '34px', height: '34px', borderRadius: '50%', border: '1px solid ' + (current ? '#2A6AA9' : (used ? '#d8dce0' : '#bbb')), background: current ? '#2A6AA9' : (used ? '#ececec' : '#fff'), color: current ? '#fff' : (used ? '#aaa' : '#222'), fontSize: '14px', fontWeight: '700', lineHeight: 1, cursor: used ? 'not-allowed' : 'pointer', opacity: used ? 0.6 : 1, padding: 0 }}
                                                                                >{k}</button>
                                                                            );
                                                                        }.bind(this))}
                                                                    </div>
                                                                </div>
                                                            </React.Fragment>
                                                        );
                                                    }.bind(this))()}
                                                </div>
                                                <span style={{ fontSize: '13px', color: '#222', fontWeight: '600', flex: 1 }}>{lang.label}</span>
                                                <span style={{ fontSize: '10px', color: '#888', fontFamily: 'monospace' }}>{lang.code}</span>
                                            </div>
                                        );
                                    }.bind(this))}
                                    <div style={{ fontSize: '11px', color: '#888', fontStyle: 'italic', marginTop: '4px' }}>
                                        Add or remove languages from the flow ⋮ menu → Languages…
                                    </div>
                                </div>
                            )}

                            <label style={labelStyle}>Next</label>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '20px' }}>
                                <select
                                    value={enType}
                                    onChange={function(e) { this.setEntryNextType(e.target.value); }.bind(this)}
                                    style={{ width: '180px', padding: '8px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '13px', background: '#fff', flexShrink: 0 }}
                                >
                                    <option value="">— Type —</option>
                                    <option value="menu">Menu Node</option>
                                    <option value="block">Block</option>
                                    <option value="action">Action</option>
                                    {enOtherFlows.length > 0 && <option value="flow">Flow</option>}
                                </select>
                                {enType === 'menu' && (
                                    <select value={enMenuVal} onChange={function(e) { this.setEntryNextTarget(e.target.value); }.bind(this)} style={enSubStyle}>
                                        <option value="">— Pick menu —</option>
                                        <option value="newMenu:default">+ New Menu Node</option>
                                        {enCandidates.filter(function(n) { return n.type === 'ivrNode'; }).map(function(n) {
                                            return <option key={n.id} value={n.id}>→ {(n.data || {}).label || n.id}</option>;
                                        })}
                                    </select>
                                )}
                                {enType === 'block' && (
                                    <select value={enBlockVal} onChange={function(e) { this.setEntryNextTarget(e.target.value); }.bind(this)} style={enSubStyle}>
                                        <option value="">— Pick block —</option>
                                        {enBlockVal && enTargetNode && <option value={enBlockVal}>→ {(enTargetNode.data || {}).label || enBlockVal}</option>}
                                        {Object.values(customBlocks).map(function(b) {
                                            return <option key={b.id} value={'newBlock:' + b.id}>+ {b.name}</option>;
                                        })}
                                    </select>
                                )}
                                {enType === 'action' && (
                                    <select value={enActionVal} onChange={function(e) { this.setEntryNextTarget(e.target.value); }.bind(this)} style={enSubStyle}>
                                        <option value="">— Pick action —</option>
                                        <option value="action:hangup">Hang Up</option>
                                        <option value="action:transfer">Transfer</option>
                                    </select>
                                )}
                                {enType === 'flow' && (
                                    <select value={enFlowVal} onChange={function(e) { this.setEntryNextTarget(e.target.value); }.bind(this)} style={enSubStyle}>
                                        <option value="">— Pick flow —</option>
                                        {enOtherFlows.map(function(f) {
                                            return <option key={f.id} value={'flow:' + f.id}>↪ {f.name || f.id}</option>;
                                        })}
                                    </select>
                                )}
                                {!enType && (
                                    <div style={{ flex: 1, fontSize: '11px', color: '#aaa', fontStyle: 'italic', padding: '0 8px' }}>Pick a type to choose what comes next</div>
                                )}
                            </div>

                            {this.state.confirmingNodeDelete && (
                                <div style={{ background: '#fff5f5', border: '1px solid #f5c6cb', borderRadius: '6px', padding: '12px 14px', marginBottom: '16px' }}>
                                    <div style={{ fontWeight: '700', color: '#c0392b', marginBottom: '6px', fontSize: '13px' }}>Delete all child nodes too?</div>
                                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>This entry has children that will also be permanently deleted.</div>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button onClick={this.doDeleteNode} style={{ background: '#c0392b', border: 'none', borderRadius: '6px', color: '#fff', padding: '7px 14px', fontSize: '13px', cursor: 'pointer', fontWeight: '700' }}>Yes, delete all</button>
                                        <button onClick={function() { this.setState({ confirmingNodeDelete: false }); }.bind(this)} style={{ background: 'none', border: '1px solid #ccc', borderRadius: '6px', color: '#555', padding: '7px 14px', fontSize: '13px', cursor: 'pointer' }}>Cancel</button>
                                    </div>
                                </div>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
                                <SaveButton onClick={this.saveNode}>Save</SaveButton>
                            </div>
                        </div>
                    </div>
                </div>
            );
        } else if (this.state.editingNode) {
            var nd = this.state.editingNode;
            var ndData = nd.data || {};
            var isLangPickerEditing = nd.type === 'languagePickerNode';
            var otherNodes = nodes.filter(function(n) { return n.id !== nd.id; });
            var ancestorIds = computeTreeAncestors(nd.id, nodes, edges);
            var ancestorNodes = otherNodes.filter(function(n) { return ancestorIds[n.id] && n.type !== 'customNode' && n.type !== 'transferNode'; });
            var activeLang = this.state.activeLang;
            var activeLangObj = (this.state.languages || []).find(function(l) { return l.code === activeLang; }) || { code: activeLang, label: activeLang };
            var multilingual = (this.state.languages || []).length > 1;
            nodeModal = (
                <div className="blackout" tabIndex="1" autoFocus>
                    <div className="pop" style={{ display: 'flex', flexDirection: 'column', width: '680px', maxWidth: '94vw', padding: '0', position: 'relative', overflow: 'hidden' }}>
                        <div className="viewCloser" onClick={this.cancel}></div>
                        <div style={{ background: '#002f5f', padding: '22px 28px 16px', color: '#fff' }}>
                            <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: '5px' }}>{isLangPickerEditing ? 'Language Picker' : 'Edit Node'}</div>
                            <div style={{ fontSize: '21px', fontWeight: '700' }}>{ndData.label || 'Node'}</div>
                            {multilingual && !isLangPickerEditing && (
                                <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginRight: '4px' }}>Editing</span>
                                    {(this.state.languages || []).map(function(l) {
                                        var active = l.code === this.state.activeLang;
                                        var hasContent = nodeHasTranslation(ndData, l.code, l.code === flowDefaultLang({ languages: this.state.languages }));
                                        return (
                                            <button
                                                key={l.code}
                                                type="button"
                                                onClick={function(c) { this.setActiveLang(c); }.bind(this, l.code)}
                                                style={{
                                                    padding: '5px 11px', fontSize: '12px', fontWeight: '600',
                                                    border: '1px solid ' + (active ? '#fff' : 'rgba(255,255,255,0.35)'),
                                                    background: active ? '#fff' : 'transparent',
                                                    color: active ? '#002f5f' : 'rgba(255,255,255,0.92)',
                                                    borderRadius: '14px', cursor: 'pointer',
                                                    display: 'inline-flex', alignItems: 'center', gap: '5px'
                                                }}
                                            >
                                                {l.label}
                                                {!hasContent && (
                                                    <span title="Missing translation" style={{ background: '#e67e22', color: '#fff', borderRadius: '8px', padding: '0 4px', fontSize: '9px', fontWeight: '700' }}>!</span>
                                                )}
                                            </button>
                                        );
                                    }.bind(this))}
                                </div>
                            )}
                            {isLangPickerEditing && (
                                <div style={{ marginTop: '8px', fontSize: '12px', color: 'rgba(255,255,255,0.85)', lineHeight: '1.45' }}>
                                    Played before any language is selected. Use a bilingual greeting (e.g. "For English, press 1. Para español, oprima 2.").
                                </div>
                            )}
                        </div>
                        <div style={{ padding: '22px 28px', overflowY: 'auto', maxHeight: 'calc(80vh - 90px)' }}>
                            <label style={labelStyle}>Label</label>
                            <input type="text" value={ndData.label || ''} onChange={this.editNodeField('label')} style={inputStyle} />

                            <label style={labelStyle}>Audio</label>
                            {(function() {
                                var mode = ndData.audioMode === 'prompt' ? 'prompt' : 'tts';
                                var segBtn = function(active) {
                                    return {
                                        flex: 1, padding: '8px 10px', fontSize: '13px', fontWeight: '600',
                                        border: '1px solid ' + (active ? '#2A6AA9' : '#ccc'),
                                        background: active ? '#2A6AA9' : '#fff',
                                        color: active ? '#fff' : '#555',
                                        cursor: 'pointer'
                                    };
                                };
                                var ttsBtn = segBtn(mode === 'tts');
                                ttsBtn.borderRadius = '6px 0 0 6px';
                                var promptBtn = segBtn(mode === 'prompt');
                                promptBtn.borderRadius = '0 6px 6px 0';
                                promptBtn.borderLeft = 'none';
                                var Prompts = this.props.data && this.props.data.Prompts;
                                return (
                                    <div style={{ marginBottom: '12px' }}>
                                        <div style={{ display: 'flex', marginBottom: '10px' }}>
                                            <button type="button" style={ttsBtn} onClick={function() { this.setNodeAudioMode('tts'); }.bind(this)}>Text-to-Speech</button>
                                            <button type="button" style={promptBtn} onClick={function() { this.setNodeAudioMode('prompt'); }.bind(this)}>Prompt</button>
                                        </div>
                                        {mode === 'tts' ? (
                                            <textarea
                                                value={isLangPickerEditing ? (ndData.ttsText || '') : readTts(ndData, activeLang)}
                                                onChange={isLangPickerEditing ? this.editNodeField('ttsText') : this.editNodeTts}
                                                rows={4}
                                                placeholder={multilingual && !isLangPickerEditing ? ('TTS for ' + activeLangObj.label) : ''}
                                                style={{ width: '100%', padding: '10px 12px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '14px', color: '#222', background: '#fff', boxSizing: 'border-box', marginBottom: '8px', resize: 'vertical' }}
                                            />
                                        ) : (
                                            Prompts && Prompts.data ? (
                                                <div style={{ border: '1px solid #d3d3d3', borderRadius: '8px', backgroundColor: '#f0f0f0', maxWidth: '440px', margin: '0 auto 8px', display: 'flex', justifyContent: 'center' }}>
                                                    <PromptPicker
                                                        prompts={JSON.parse(JSON.stringify(Prompts))}
                                                        currentPrompt={isLangPickerEditing ? ndData.prompt : readPrompt(ndData, activeLang)}
                                                        selectionChange={this.setNodePrompt}
                                                        ws={this.props.ws}
                                                    />
                                                </div>
                                            ) : (
                                                <div style={{ padding: '10px 12px', border: '1px dashed #ccc', borderRadius: '6px', fontSize: '13px', color: '#888', marginBottom: '8px' }}>
                                                    No prompts available. Add prompts from the Prompts page first.
                                                </div>
                                            )
                                        )}
                                    </div>
                                );
                            }.bind(this))()}

                            <label style={labelStyle}>Options</label>
                            {(ndData.options || []).map(function(opt, idx) {
                                var alternatives = isLangPickerEditing ? (opt.voiceAlternatives || []) : readAlternatives(opt, activeLang);
                                var targetNode = opt.targetNodeId ? nodes.find(function(n) { return n.id === opt.targetNodeId; }) : null;
                                var derivedType = '';
                                if (opt.targetFlowId) {
                                    derivedType = 'flow';
                                } else if (opt.action) {
                                    derivedType = 'action';
                                } else if (targetNode) {
                                    if (targetNode.type === 'transferNode') derivedType = 'action';
                                    else if (targetNode.type === 'customNode') derivedType = 'block';
                                    else derivedType = 'menu';
                                }
                                var hint = this.state.optionTypeHints[idx];
                                var displayedType = (hint != null && hint !== '') ? hint : derivedType;
                                var actionVal = opt.action ? ('action:' + opt.action) :
                                    (targetNode && targetNode.type === 'transferNode' ? 'action:transfer' : '');
                                var menuVal = (targetNode && targetNode.type === 'ivrNode') ? targetNode.id : '';
                                var blockVal = (targetNode && targetNode.type === 'customNode') ? targetNode.id : '';
                                var flowVal = opt.targetFlowId ? ('flow:' + opt.targetFlowId) : '';
                                var linkedMenuChild = (menuVal && !ancestorIds[menuVal]) ? targetNode : null;
                                var activeFlowId = this.state.activeFlowId;
                                var otherFlows = Object.keys(flows).filter(function(fid) { return fid !== activeFlowId; }).map(function(fid) { return flows[fid]; });
                                var subStyle = { flex: 1, padding: '8px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '13px', background: '#fff' };
                                return (
                                    <div key={idx} style={{ border: '1px solid #e3e7ee', borderRadius: '8px', padding: '12px', marginBottom: '10px', background: '#fafbfd' }}>
                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                                            <div style={{ position: 'relative', flexShrink: 0 }}>
                                                <button
                                                    type="button"
                                                    onClick={function(idx2, e) { e.stopPropagation(); this.toggleKeypad(idx2); }.bind(this, idx)}
                                                    title="Pick key"
                                                    style={{ width: '46px', height: '34px', padding: 0, border: '1px solid ' + (this.state.keypadOpenIdx === idx ? '#2A6AA9' : '#ccc'), borderRadius: '6px', fontSize: '15px', fontWeight: '700', textAlign: 'center', background: this.state.keypadOpenIdx === idx ? '#eaf2fb' : '#fff', color: opt.key ? '#222' : '#aaa', cursor: 'pointer' }}
                                                >{opt.key || '?'}</button>
                                                {this.state.keypadOpenIdx === idx && (function() {
                                                    var usedKeys = {};
                                                    (ndData.options || []).forEach(function(o, oi) { if (oi !== idx && o.key) usedKeys[o.key] = true; });
                                                    return (
                                                        <React.Fragment>
                                                            <div
                                                                onMouseDown={function(e) { e.stopPropagation(); this.setState({ keypadOpenIdx: null }); }.bind(this)}
                                                                style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 2199, background: 'transparent' }}
                                                            />
                                                            <div
                                                                onMouseDown={function(e) { e.stopPropagation(); }}
                                                                style={{ position: 'absolute', top: '38px', left: 0, zIndex: 2200, padding: '8px', background: '#fff', border: '1px solid #ccc', borderRadius: '8px', boxShadow: '0 4px 14px rgba(0,0,0,0.18)' }}
                                                            >
                                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 34px)', gap: '6px' }}>
                                                                    {KEYPAD_KEYS.map(function(k) {
                                                                        var used = !!usedKeys[k];
                                                                        var current = k === opt.key;
                                                                        return (
                                                                            <button
                                                                                key={k}
                                                                                type="button"
                                                                                disabled={used}
                                                                                onClick={used ? undefined : function(idx2, kk) { this.setOptionKey(idx2, kk); }.bind(this, idx, k)}
                                                                                style={{
                                                                                    width: '34px', height: '34px', borderRadius: '50%',
                                                                                    border: '1px solid ' + (current ? '#2A6AA9' : (used ? '#d8dce0' : '#bbb')),
                                                                                    background: current ? '#2A6AA9' : (used ? '#ececec' : '#fff'),
                                                                                    color: current ? '#fff' : (used ? '#aaa' : '#222'),
                                                                                    fontSize: '14px', fontWeight: '700', lineHeight: 1,
                                                                                    cursor: used ? 'not-allowed' : 'pointer',
                                                                                    opacity: used ? 0.6 : 1,
                                                                                    padding: 0
                                                                                }}
                                                                            >{k}</button>
                                                                        );
                                                                    }.bind(this))}
                                                                </div>
                                                            </div>
                                                        </React.Fragment>
                                                    );
                                                }.bind(this))()}
                                            </div>
                                            <input
                                                type="text" placeholder="Name (e.g. Sales)" value={opt.label || ''}
                                                onChange={function(idx2, e) { this.editNodeOption(idx2, 'label', e.target.value); }.bind(this, idx)}
                                                style={{ flex: 1, padding: '8px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '13px', background: '#fff' }}
                                            />
                                            <button
                                                onClick={function(idx2) { this.removeOption(idx2); }.bind(this, idx)}
                                                style={{ background: 'none', border: 'none', color: '#c0392b', fontSize: '18px', cursor: 'pointer', flexShrink: 0, lineHeight: 1 }}
                                                title="Remove option"
                                            >×</button>
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                            {isLangPickerEditing ? (
                                                <React.Fragment>
                                                    <select
                                                        value={opt.setsLanguage || ''}
                                                        onChange={function(idx2, e) { this.editPickerOptionLang(idx2, e.target.value); }.bind(this, idx)}
                                                        style={{ width: '180px', padding: '8px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '13px', background: '#fff', flexShrink: 0 }}
                                                    >
                                                        <option value="">— Language —</option>
                                                        {(this.state.languages || []).map(function(l) {
                                                            return <option key={l.code} value={l.code}>{l.label} ({l.code})</option>;
                                                        })}
                                                    </select>
                                                    <select
                                                        value={opt.targetNodeId || ''}
                                                        onChange={function(idx2, e) { this.editPickerOptionTarget(idx2, e.target.value); }.bind(this, idx)}
                                                        style={subStyle}
                                                    >
                                                        <option value="">— Continue to —</option>
                                                        {nodes.filter(function(n) { return n.id !== nd.id && n.type !== 'languagePickerNode'; }).map(function(n) {
                                                            return <option key={n.id} value={n.id}>→ {(n.data || {}).label || n.id}</option>;
                                                        })}
                                                    </select>
                                                </React.Fragment>
                                            ) : (
                                                <React.Fragment>
                                                    <select
                                                        value={displayedType}
                                                        onChange={function(idx2, e) { this.setOptionType(idx2, e.target.value); }.bind(this, idx)}
                                                        style={{ width: '140px', padding: '8px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '13px', background: '#fff', flexShrink: 0 }}
                                                    >
                                                        <option value="">— Type —</option>
                                                        <option value="menu">Menu Node</option>
                                                        <option value="block">Block</option>
                                                        <option value="action">Action</option>
                                                        {otherFlows.length > 0 && <option value="flow">Flow</option>}
                                                    </select>
                                                    {displayedType === 'flow' && (
                                                        <select value={flowVal} onChange={function(idx2, e) { this.editNodeOptionTarget(idx2, e.target.value); }.bind(this, idx)} style={subStyle}>
                                                            <option value="">— Pick flow —</option>
                                                            {otherFlows.map(function(f) {
                                                                return <option key={f.id} value={'flow:' + f.id}>↪ {f.name || f.id}</option>;
                                                            })}
                                                        </select>
                                                    )}
                                                    {displayedType === 'menu' && (
                                                        <select value={menuVal} onChange={function(idx2, e) { this.editNodeOptionTarget(idx2, e.target.value); }.bind(this, idx)} style={subStyle}>
                                                            <option value="">— Pick menu —</option>
                                                            <option value="newMenu:default">+ New Menu Node</option>
                                                            {linkedMenuChild && <option value={linkedMenuChild.id}>→ {(linkedMenuChild.data || {}).label || linkedMenuChild.id}</option>}
                                                            {ancestorNodes.length > 0 && <option disabled>── Return to Parent ──</option>}
                                                            {ancestorNodes.map(function(n) {
                                                                return <option key={'anc_' + n.id} value={n.id}>↑ {(n.data || {}).label || n.id}</option>;
                                                            })}
                                                        </select>
                                                    )}
                                                    {displayedType === 'block' && (
                                                        <select value={blockVal} onChange={function(idx2, e) { this.editNodeOptionTarget(idx2, e.target.value); }.bind(this, idx)} style={subStyle}>
                                                            <option value="">— Pick block —</option>
                                                            {blockVal && targetNode && <option value={blockVal}>→ {(targetNode.data || {}).label || blockVal}</option>}
                                                            {Object.values(customBlocks).map(function(b) {
                                                                return <option key={b.id} value={'newBlock:' + b.id}>+ {b.name}</option>;
                                                            })}
                                                        </select>
                                                    )}
                                                    {displayedType === 'action' && (
                                                        <select value={actionVal} onChange={function(idx2, e) { this.editNodeOptionTarget(idx2, e.target.value); }.bind(this, idx)} style={subStyle}>
                                                            <option value="">— Pick action —</option>
                                                            <option value="action:repeat">Repeat</option>
                                                            <option value="action:hangup">Hang Up</option>
                                                            <option value="action:transfer">Transfer</option>
                                                        </select>
                                                    )}
                                                    {!displayedType && (
                                                        <div style={{ flex: 1, fontSize: '11px', color: '#aaa', fontStyle: 'italic', padding: '0 8px' }}>Pick a type to choose a destination</div>
                                                    )}
                                                </React.Fragment>
                                            )}
                                        </div>
                                        <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                                            <button
                                                onClick={function(oi) { this.setState({ editingAlternativesIdx: oi }); }.bind(this, idx)}
                                                style={{ background: 'none', border: '1px solid #2A6AA9', borderRadius: '5px', color: '#2A6AA9', fontSize: '12px', padding: '5px 12px', cursor: 'pointer', flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                                            >
                                                <span style={{ display: 'inline-block', width: '14px', height: '14px', backgroundColor: '#2A6AA9', WebkitMaskImage: 'url(' + speechIcon + ')', maskImage: 'url(' + speechIcon + ')', WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat', WebkitMaskPosition: 'center', maskPosition: 'center', WebkitMaskSize: 'contain', maskSize: 'contain' }} />
                                                Voice Options
                                            </button>
                                            <span style={{ fontSize: '11px', color: '#888', textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                                                {(function() {
                                                    var nonEmpty = alternatives.filter(function(a) { return a && a.trim(); });
                                                    if (nonEmpty.length === 0) return 'none';
                                                    var shown = nonEmpty.slice(0, 10).join(', ');
                                                    var extra = nonEmpty.length > 10 ? ' +' + (nonEmpty.length - 10) + ' more' : '';
                                                    return shown + extra;
                                                })()}
                                            </span>
                                        </div>
                                    </div>
                                );
                            }.bind(this))}
                            <button onClick={this.addOption} style={{ background: 'none', border: '1px solid #2A6AA9', borderRadius: '6px', color: '#2A6AA9', fontSize: '13px', padding: '6px 14px', cursor: 'pointer', marginBottom: '20px' }}>+ Add Option</button>

                            <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={labelStyle}>No Input</label>
                                    <select
                                        value={(ndData.noInput || {}).action || 'repeat'}
                                        onChange={function(e) { this.editFallback('noInput', e.target.value); }.bind(this)}
                                        style={{ width: '100%', padding: '9px 10px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '14px', background: '#fff' }}
                                    >
                                        <option value="repeat">Repeat</option>
                                        <option value="hangup">Hang Up</option>
                                        <option value="transfer">Transfer</option>
                                    </select>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={labelStyle}>No Match</label>
                                    <select
                                        value={(ndData.noMatch || {}).action || 'repeat'}
                                        onChange={function(e) { this.editFallback('noMatch', e.target.value); }.bind(this)}
                                        style={{ width: '100%', padding: '9px 10px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '14px', background: '#fff' }}
                                    >
                                        <option value="repeat">Repeat</option>
                                        <option value="hangup">Hang Up</option>
                                        <option value="transfer">Transfer</option>
                                    </select>
                                </div>
                            </div>

                            {this.state.confirmingNodeDelete && (
                                <div style={{ background: '#fff5f5', border: '1px solid #f5c6cb', borderRadius: '6px', padding: '12px 14px', marginBottom: '16px' }}>
                                    <div style={{ fontWeight: '700', color: '#c0392b', marginBottom: '6px', fontSize: '13px' }}>Delete all child nodes too?</div>
                                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>This node has children that will also be permanently deleted.</div>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button
                                            onClick={this.doDeleteNode}
                                            style={{ background: '#c0392b', border: 'none', borderRadius: '6px', color: '#fff', padding: '7px 14px', fontSize: '13px', cursor: 'pointer', fontWeight: '700' }}
                                        >Yes, delete all</button>
                                        <button
                                            onClick={function() { this.setState({ confirmingNodeDelete: false }); }.bind(this)}
                                            style={{ background: 'none', border: '1px solid #ccc', borderRadius: '6px', color: '#555', padding: '7px 14px', fontSize: '13px', cursor: 'pointer' }}
                                        >Cancel</button>
                                    </div>
                                </div>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
                                {nodes.length > 1 && <DeleteButton onClick={this.requestDeleteNode} />}
                                <SaveButton onClick={this.saveNode}>Save Node</SaveButton>
                            </div>
                        </div>
                    </div>
                </div>
            );
        } // end else if editingNode

        // ── Alternatives popup ─────────────────────────────────────────────────
        var alternativesModal = null;
        if (this.state.editingNode && this.state.editingAlternativesIdx !== null) {
            var altOpt = ((this.state.editingNode.data || {}).options || [])[this.state.editingAlternativesIdx];
            if (altOpt) {
                var altIsLangPicker = this.state.editingNode.type === 'languagePickerNode';
                var alts = altIsLangPicker ? (altOpt.voiceAlternatives || []) : readAlternatives(altOpt, this.state.activeLang);
                var altIdxOuter = this.state.editingAlternativesIdx;
                var altActiveLangObj = (this.state.languages || []).find(function(l) { return l.code === this.state.activeLang; }.bind(this)) || { code: this.state.activeLang, label: this.state.activeLang };
                alternativesModal = (
                    <div className="blackout" style={{ zIndex: 2100 }} tabIndex="1" autoFocus>
                        <div className="pop" style={{ display: 'flex', flexDirection: 'column', width: '460px', maxWidth: '94vw', padding: '0', position: 'relative', overflow: 'hidden' }}>
                            <div className="viewCloser" onClick={function() { this.setState({ editingAlternativesIdx: null }); }.bind(this)}></div>
                            <div style={{ background: '#002f5f', padding: '22px 28px 16px', color: '#fff', textAlign: 'center' }}>
                                <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: '5px' }}>Voice Options</div>
                                <div style={{ fontSize: '21px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                                    <img src={speechIcon} alt="" style={{ width: '22px', height: '22px', filter: 'brightness(0) invert(1)' }} />
                                    {altOpt.label || 'Option'}
                                </div>
                                {!altIsLangPicker && (this.state.languages || []).length > 1 && (
                                    <div style={{ marginTop: '10px', display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'rgba(255,255,255,0.14)', padding: '4px 10px', borderRadius: '14px', fontSize: '11px', fontWeight: '600' }}>
                                        <span style={{ opacity: 0.7 }}>Editing:</span>
                                        <span>{altActiveLangObj.label}</span>
                                    </div>
                                )}
                            </div>
                            <div style={{ padding: '22px 28px', overflowY: 'auto', maxHeight: 'calc(80vh - 90px)' }}>
                                <div style={{ fontSize: '12px', color: '#666', marginBottom: '14px', lineHeight: '1.45' }}>
                                    List phrases the caller might say to choose <strong>{altOpt.label || 'this option'}</strong>. The name above is matched automatically.
                                </div>
                                {alts.length === 0 && (
                                    <div style={{ fontSize: '12px', color: '#aaa', fontStyle: 'italic', marginBottom: '12px' }}>No options yet.</div>
                                )}
                                {alts.map(function(alt, altIdx) {
                                    return (
                                        <div key={altIdx} style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '6px' }}>
                                            <input
                                                type="text" placeholder="e.g. purchase, buy, buying" value={alt}
                                                data-alt={altIdxOuter + '_' + altIdx}
                                                autoFocus={altIdx === alts.length - 1 && !alt}
                                                onChange={function(ai, e) { this.editAlternative(altIdxOuter, ai, e.target.value); }.bind(this, altIdx)}
                                                onKeyDown={function(ai, e) { if (e.key === 'Enter') { e.preventDefault(); this.insertAlternativeAfter(altIdxOuter, ai); } }.bind(this, altIdx)}
                                                style={{ flex: 1, padding: '8px 10px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '13px', background: '#fff' }}
                                            />
                                            <button
                                                onClick={function(ai) { this.removeAlternative(altIdxOuter, ai); }.bind(this, altIdx)}
                                                style={{ background: 'none', border: 'none', color: '#c0392b', fontSize: '18px', cursor: 'pointer', flexShrink: 0, lineHeight: 1 }}
                                            >×</button>
                                        </div>
                                    );
                                }.bind(this))}
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '4px', marginBottom: '18px', flexWrap: 'wrap' }}>
                                    <button
                                        onClick={function() { this.addAlternative(altIdxOuter); }.bind(this)}
                                        style={{ background: 'none', border: '1px dashed #2A6AA9', borderRadius: '6px', color: '#2A6AA9', fontSize: '13px', padding: '6px 14px', cursor: 'pointer' }}
                                    >+ Add Option</button>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'center' }}>
                                    <button
                                        className="button"
                                        style={{ border: 'none', cursor: 'pointer' }}
                                        onClick={function() { this.setState({ editingAlternativesIdx: null }); }.bind(this)}
                                    >Done</button>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            }
        }

        // ── Add flow modal ─────────────────────────────────────────────────────
        var addFlowModal = this.state.addingFlow ? (
            <div className="blackout" tabIndex="1" autoFocus>
                <div className="pop" style={{ display: 'flex', flexDirection: 'column', width: '400px', maxWidth: '94vw', padding: '0', position: 'relative', overflow: 'hidden' }}>
                    <div className="viewCloser" onClick={this.cancel}></div>
                    <div style={{ background: '#002f5f', padding: '22px 28px 16px', color: '#fff' }}>
                        <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: '5px' }}>New IVR Flow</div>
                        <div style={{ fontSize: '21px', fontWeight: '700' }}>Create Flow</div>
                    </div>
                    <div style={{ padding: '22px 28px' }}>
                        <label style={labelStyle}>Flow Name</label>
                        <input
                            type="text" value={this.state.newFlowName} autoFocus
                            onChange={function(e) { this.setState({ newFlowName: e.target.value, error: '' }); }.bind(this)}
                            onKeyDown={function(e) { if (e.key === 'Enter') this.confirmAddFlow(); }.bind(this)}
                            placeholder="e.g. Main Menu" style={inputStyle}
                        />
                        {this.state.error && <div className="error" style={{ marginBottom: '12px' }}>{this.state.error}</div>}
                        <button className="button" style={{ border: 'none', cursor: 'pointer' }} onClick={this.confirmAddFlow}>Create</button>
                    </div>
                </div>
            </div>
        ) : null;

        // ── Rename modal ───────────────────────────────────────────────────────
        var renameModal = this.state.renamingFlow ? (
            <div className="blackout" tabIndex="1" autoFocus>
                <div className="pop" style={{ display: 'flex', flexDirection: 'column', width: '400px', maxWidth: '94vw', padding: '0', position: 'relative', overflow: 'hidden' }}>
                    <div className="viewCloser" onClick={this.cancel}></div>
                    <div style={{ background: '#002f5f', padding: '22px 28px 16px', color: '#fff' }}>
                        <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: '5px' }}>Rename Flow</div>
                        <div style={{ fontSize: '21px', fontWeight: '700' }}>{activeFlow.name || ''}</div>
                    </div>
                    <div style={{ padding: '22px 28px' }}>
                        <label style={labelStyle}>New Name</label>
                        <input
                            type="text" value={this.state.newFlowName} autoFocus
                            onChange={function(e) { this.setState({ newFlowName: e.target.value, error: '' }); }.bind(this)}
                            onKeyDown={function(e) { if (e.key === 'Enter') this.confirmRenameFlow(); }.bind(this)}
                            style={inputStyle}
                        />
                        {this.state.error && <div className="error" style={{ marginBottom: '12px' }}>{this.state.error}</div>}
                        {this.state.loading
                            ? <div className="loader"></div>
                            : <SaveButton onClick={this.confirmRenameFlow} />
                        }
                    </div>
                </div>
            </div>
        ) : null;

        // ── Delete modal ───────────────────────────────────────────────────────
        var deleteModal = this.state.deletingFlow ? (
            <div className="blackout" tabIndex="1" autoFocus>
                <div className="pop" style={{ display: 'flex', flexDirection: 'column', width: '400px', maxWidth: '94vw', padding: '0', position: 'relative', overflow: 'hidden' }}>
                    <div className="viewCloser" onClick={this.cancel}></div>
                    <div style={{ background: '#002f5f', padding: '22px 28px 16px', color: '#fff' }}>
                        <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: '5px' }}>Delete Flow</div>
                        <div style={{ fontSize: '21px', fontWeight: '700' }}>{activeFlow.name || ''}</div>
                    </div>
                    <div style={{ padding: '22px 28px' }}>
                        <p style={{ color: '#555', marginBottom: '20px' }}>Are you sure you want to delete <strong>{activeFlow.name}</strong>? This cannot be undone.</p>
                        {this.state.loading
                            ? <div className="loader"></div>
                            : <DeleteButton onClick={this.confirmDeleteFlow} />
                        }
                    </div>
                </div>
            </div>
        ) : null;

        // ── Canvas / empty state ───────────────────────────────────────────────
        var canvasContent;
        if (flowKeys.length === 0) {
            canvasContent = (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#aaa', gap: '14px' }}>
                    <div style={{ fontSize: '40px' }}>☎</div>
                    <div style={{ fontSize: '15px', fontWeight: '600' }}>No IVR flows yet</div>
                    <div style={{ fontSize: '13px' }}>Create a new flow or generate one from a virtual agent.</div>
                    <button className="button" style={{ border: 'none', cursor: 'pointer' }} onClick={this.openAddFlow}>+ Create Flow</button>
                </div>
            );
        } else {
            var zoom = this.state.zoom;
            var panX = this.state.panX;
            var panY = this.state.panY;
            canvasContent = (
                <div
                    ref={function(el) { this._containerRef = el; }.bind(this)}
                    style={{ flex: 1, minHeight: 0, overflow: 'hidden', background: '#f4f6fb', position: 'relative', cursor: 'grab' }}
                    onMouseDown={this.onCanvasMouseDown}
                    onMouseMove={this.onCanvasMouseMove}
                    onMouseUp={this.onCanvasMouseUp}
                    onMouseLeave={this.onCanvasMouseUp}
                >
                    <div
                        ref={function(el) { this._canvasRef = el; }.bind(this)}
                        style={{ position: 'absolute', top: 0, left: 0, width: canvasW, height: canvasH, transform: 'translate(' + panX + 'px,' + panY + 'px) scale(' + zoom + ')', transformOrigin: '0 0' }}
                    >
                        <EdgeLines edges={edges} positions={positions} canvasW={canvasW} canvasH={canvasH} heights={this.state.nodeHeights} />
                        {visibleNodes.map(function(node) {
                            var pos = positions[node.id] || { x: 0, y: 0 };
                            if (node.type === 'transferNode') {
                                return (
                                    <TransferNodeCard
                                        key={node.id}
                                        node={node}
                                        position={pos}
                                        color={colorMap[node.id]}
                                        dtmfKey={keyMap[node.id]}
                                        selected={this.state.selectedNodeId === node.id}
                                        onClick={this.openEditNode}
                                        queuesList={queuesList}
                                        onMeasure={this.measureNode}
                                    />
                                );
                            }
                            if (node.type === 'customNode') {
                                return (
                                    <CustomBlockCard
                                        key={node.id}
                                        node={node}
                                        position={pos}
                                        block={customBlocks[(node.data || {}).blockId]}
                                        dtmfKey={keyMap[node.id]}
                                        selected={this.state.selectedNodeId === node.id}
                                        onClick={this.openEditNode}
                                        allNodes={nodes}
                                        allFlows={flows}
                                        onMeasure={this.measureNode}
                                    />
                                );
                            }
                            if (node.type === 'languagePickerNode') {
                                return (
                                    <LanguagePickerCard
                                        key={node.id}
                                        node={node}
                                        position={pos}
                                        languages={this.state.languages}
                                        selected={this.state.selectedNodeId === node.id}
                                        onClick={this.openEditNode}
                                        allNodes={nodes}
                                        onMeasure={this.measureNode}
                                    />
                                );
                            }
                            if (node.type === 'entryNode') {
                                return (
                                    <EntryNodeCard
                                        key={node.id}
                                        node={node}
                                        position={pos}
                                        selected={this.state.selectedNodeId === node.id}
                                        onClick={this.openEditNode}
                                        allNodes={nodes}
                                        allFlows={flows}
                                        onMeasure={this.measureNode}
                                        activeLang={this.state.activeLang}
                                        isMultilingual={(this.state.languages || []).length > 1}
                                        isDefaultLang={this.state.activeLang === flowDefaultLang(activeFlow)}
                                        languages={this.state.languages}
                                    />
                                );
                            }
                            return (
                                <NodeCard
                                    key={node.id}
                                    node={node}
                                    position={pos}
                                    color={colorMap[node.id]}
                                    dtmfKey={keyMap[node.id]}
                                    selected={this.state.selectedNodeId === node.id}
                                    onClick={this.openEditNode}
                                    onAddChild={this.addChildNode}
                                    allNodes={nodes}
                                    allFlows={flows}
                                    descendantCount={descendantCounts[node.id] || 0}
                                    collapsed={!!collapsedNodes[node.id]}
                                    onToggleCollapse={this.toggleCollapseNode}
                                    onMeasure={this.measureNode}
                                    activeLang={this.state.activeLang}
                                    isMultilingual={(this.state.languages || []).length > 1}
                                    isDefaultLang={this.state.activeLang === flowDefaultLang(activeFlow)}
                                />
                            );
                        }.bind(this))}
                    </div>
                    <MiniMap
                        positions={positions}
                        edges={edges}
                        canvasW={canvasW}
                        canvasH={canvasH}
                        zoom={zoom}
                        panX={panX}
                        panY={panY}
                        containerW={this._containerRef ? this._containerRef.clientWidth : 600}
                        containerH={this._containerRef ? this._containerRef.clientHeight : 400}
                        colorMap={colorMap}
                        onClickPan={function(nx, ny) { this.setState({ panX: nx, panY: ny }); }.bind(this)}
                    />
                </div>
            );
        }

        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
                {/* Toolbar */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px', borderBottom: '1px solid #e0e6ef', background: '#fff', flexShrink: 0 }}>
                    {flowKeys.length > 0 && (
                        <select
                            value={this.state.activeFlowId}
                            onChange={function(e) { this.switchFlow(e.target.value); }.bind(this)}
                            style={{ height: '38px', padding: '0 10px', border: '1px solid #ddd', borderRadius: '4px', fontWeight: 'bold', fontSize: '16px', color: '#137', minWidth: '200px' }}
                        >
                            {flowKeys.map(function(k) {
                                return <option key={k} value={k}>{flows[k].name || k}</option>;
                            })}
                        </select>
                    )}

                    <button onClick={this.openAddFlow} style={{ height: '38px', width: '38px', padding: 0, borderRadius: '4px', border: '1px solid #ddd', background: '#fff', fontSize: '20px', color: '#2A6AA9', cursor: 'pointer', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }} title="Add new flow">+</button>

                    {flowKeys.length > 0 && this.state.activeFlowId && (
                        <div style={{ position: 'relative' }}>
                            <button
                                onClick={function() { this.setState(function(s) { return { flowMenuOpen: !s.flowMenuOpen }; }); }.bind(this)}
                                onBlur={function() { setTimeout(function() { this.setState({ flowMenuOpen: false }); }.bind(this), 150); }.bind(this)}
                                style={{ height: '38px', width: '38px', padding: 0, borderRadius: '4px', border: '1px solid #ddd', background: this.state.flowMenuOpen ? '#f0f4fa' : '#fff', fontSize: '20px', color: '#555', cursor: 'pointer', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}
                            >⋮</button>
                            {this.state.flowMenuOpen && (
                                <div style={{ position: 'absolute', top: '42px', left: 0, background: '#fff', border: '1px solid #ddd', borderRadius: '6px', boxShadow: '0 4px 16px rgba(0,0,0,0.13)', zIndex: 100, minWidth: '160px', overflow: 'hidden' }}>
                                    <button onClick={this.openRenameFlow} style={{ display: 'block', width: '100%', padding: '10px 16px', background: 'none', border: 'none', borderBottom: '1px solid #eee', textAlign: 'left', cursor: 'pointer', fontSize: '14px', color: '#222' }}>Rename</button>
                                    <button onClick={this.duplicateFlow} style={{ display: 'block', width: '100%', padding: '10px 16px', background: 'none', border: 'none', borderBottom: '1px solid #eee', textAlign: 'left', cursor: 'pointer', fontSize: '14px', color: '#222' }}>Duplicate</button>
                                    <button onMouseDown={function(e) { e.preventDefault(); this.openManageLanguages(); }.bind(this)} style={{ display: 'block', width: '100%', padding: '10px 16px', background: 'none', border: 'none', borderBottom: '1px solid #eee', textAlign: 'left', cursor: 'pointer', fontSize: '14px', color: '#222' }}>Languages…</button>
                                    <button onClick={this.openDeleteFlow} style={{ display: 'block', width: '100%', padding: '10px 16px', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '14px', color: '#c0392b' }}>Delete</button>
                                </div>
                            )}
                        </div>
                    )}

                    <div style={{ flex: 1 }} />

                    {/* Zoom controls */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#f5f7fa', border: '1px solid #ddd', borderRadius: '6px', padding: '2px 4px' }}>
                        <button
                            onClick={function() { this.setState(function(s) { return { zoom: Math.max(0.25, +(s.zoom - 0.25).toFixed(2)) }; }); }.bind(this)}
                            style={{ background: 'none', border: 'none', fontSize: '16px', color: '#555', cursor: 'pointer', width: '28px', height: '28px', borderRadius: '4px', lineHeight: 1 }}
                        >−</button>
                        <span style={{ fontSize: '12px', color: '#555', fontWeight: '600', minWidth: '38px', textAlign: 'center' }}>{Math.round(this.state.zoom * 100)}%</span>
                        <button
                            onClick={function() { this.setState(function(s) { return { zoom: Math.min(2, +(s.zoom + 0.25).toFixed(2)) }; }); }.bind(this)}
                            style={{ background: 'none', border: 'none', fontSize: '16px', color: '#555', cursor: 'pointer', width: '28px', height: '28px', borderRadius: '4px', lineHeight: 1 }}
                        >+</button>
                        <button
                            onClick={function() { this.setState({ zoom: 1 }); }.bind(this)}
                            style={{ background: 'none', border: 'none', fontSize: '11px', color: '#888', cursor: 'pointer', padding: '0 4px', borderLeft: '1px solid #ddd', height: '20px' }}
                            title="Reset zoom"
                        >reset</button>
                    </div>

                    {this.state.isDirty && (
                        <React.Fragment>
                            <span style={{ fontSize: '12px', color: '#888' }}>Unsaved changes</span>
                            {this.state.loading
                                ? <div className="loader" style={{ margin: 0 }}></div>
                                : <SaveButton onClick={this.saveFlow} />
                            }
                        </React.Fragment>
                    )}
                </div>

                {canvasContent}

                {nodeModal}
                {alternativesModal}
                {addFlowModal}
                {renameModal}
                {deleteModal}
                {this.renderLanguagesModal()}
            </div>
        );
    }

    renderLanguagesModal() {
        if (!this.state.managingLanguages) return null;
        var languages = this.state.languages || [];
        var existingCodes = {}; languages.forEach(function(l) { existingCodes[l.code] = true; });
        var addable = COMMON_LANGUAGES.filter(function(c) { return !existingCodes[c.code]; });
        return (
            <div className="blackout" tabIndex="1" autoFocus>
                <div className="pop" style={{ display: 'flex', flexDirection: 'column', width: '520px', maxWidth: '94vw', padding: '0', position: 'relative', overflow: 'hidden' }}>
                    <div className="viewCloser" onClick={this.cancel}></div>
                    <div style={{ background: '#002f5f', padding: '22px 28px 16px', color: '#fff' }}>
                        <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: '5px' }}>Languages</div>
                        <div style={{ fontSize: '21px', fontWeight: '700' }}>🌐 Flow Languages</div>
                        <div style={{ marginTop: '6px', fontSize: '12px', color: 'rgba(255,255,255,0.85)', lineHeight: '1.45' }}>
                            Adding a second language inserts a Language Selection node at the start of the flow. All other nodes are shared across languages.
                        </div>
                    </div>
                    <div style={{ padding: '20px 28px', overflowY: 'auto', maxHeight: 'calc(80vh - 110px)' }}>
                        <label style={labelStyle}>Configured</label>
                        {languages.map(function(l) {
                            return (
                                <div key={l.code} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', border: '1px solid #e3e7ee', borderRadius: '8px', marginBottom: '8px', background: '#fafbfd' }}>
                                    <input
                                        type="text" value={l.label}
                                        onChange={function(c, e) { this.renameLanguage(c, e.target.value); }.bind(this, l.code)}
                                        style={{ flex: 1, padding: '7px 10px', border: '1px solid #ccc', borderRadius: '5px', fontSize: '13px', background: '#fff' }}
                                    />
                                    <span style={{ fontSize: '11px', color: '#888', fontFamily: 'monospace', minWidth: '50px' }}>{l.code}</span>
                                    {l.isDefault ? (
                                        <span style={{ fontSize: '10px', fontWeight: '700', color: '#2A6AA9', letterSpacing: '0.5px' }}>DEFAULT</span>
                                    ) : (
                                        <button onClick={function(c) { this.setDefaultLanguage(c); }.bind(this, l.code)} style={{ background: 'none', border: '1px solid #ccc', borderRadius: '5px', color: '#666', fontSize: '11px', padding: '4px 8px', cursor: 'pointer' }}>Set default</button>
                                    )}
                                    {languages.length > 1 && !l.isDefault && (
                                        <button onClick={function(c) { this.removeLanguageFromFlow(c); }.bind(this, l.code)} style={{ background: 'none', border: 'none', color: '#c0392b', fontSize: '18px', cursor: 'pointer', flexShrink: 0, lineHeight: 1 }}>×</button>
                                    )}
                                </div>
                            );
                        }.bind(this))}
                        {addable.length > 0 && (
                            <div style={{ marginTop: '14px' }}>
                                <label style={labelStyle}>Add Language</label>
                                <select
                                    value=""
                                    onChange={function(e) {
                                        if (!e.target.value) return;
                                        var picked = COMMON_LANGUAGES.find(function(c) { return c.code === e.target.value; });
                                        if (picked) this.addLanguageToFlow(picked.code, picked.label);
                                    }.bind(this)}
                                    style={inputStyle}
                                >
                                    <option value="">— Pick a language to add —</option>
                                    {addable.map(function(c) {
                                        return <option key={c.code} value={c.code}>{c.label} ({c.code})</option>;
                                    })}
                                </select>
                            </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '8px' }}>
                            <button className="button" style={{ border: 'none', cursor: 'pointer' }} onClick={this.cancel}>Done</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

const icon = "menuWorkflow";
const roles = ["View", "Add", "Edit", "Delete"];
export { IVRFlows, icon, roles };
